import axios from 'axios';
import JSZip from 'jszip';


// Create base axios instance for main API
const api = axios.create({
    baseURL: 'https://accountss-3.onrender.com',
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true,
    timeout: 300000 // 5 minutes
});

// Create Python service instance with improved configuration
// Update Python service configuration
const pythonApi = axios.create({
    baseURL: 'https://accountss-loams.onrender.com',
    timeout: 600000,
    withCredentials: true,
    headers: {
        'Accept': '*/*',
        'Content-Type': 'application/json'
    },
    maxContentLength: 50 * 1024 * 1024,
    maxBodyLength: 50 * 1024 * 1024
});

// Add request interceptor for authentication and logging
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = token;
        }
        console.log('[API] Request:', {
            url: config.baseURL + config.url,
            method: config.method,
            hasToken: !!token
        });
        return config;
    },
    (error) => {
        console.error('[API] Request error:', error);
        return Promise.reject(error);
    }
);

// Enhanced request interceptor for LOAMS service
pythonApi.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = token;
        }

        // Add retry attempt tracking
        config.retryAttempt = config.retryAttempt || 0;

        console.log('[LOAMS] Making request:', {
            url: config.url,
            method: config.method,
            contentType: config.headers['Content-Type'],
            retryAttempt: config.retryAttempt
        });
        return config;
    },
    error => {
        console.error('[LOAMS] Request error:', error);
        return Promise.reject(error);
    }
);

// Enhanced response interceptor for LOAMS service
pythonApi.interceptors.response.use(
    response => {
        console.log('[LOAMS] Response received:', {
            url: response.config.url,
            status: response.status,
            contentType: response.headers['content-type']
        });
        return response;
    },
    async error => {
        console.error('[LOAMS] Response error:', {
            url: error.config?.url,
            status: error.response?.status,
            message: error.message,
            retryAttempt: error.config?.retryAttempt
        });

        // Implement retry logic
        const config = error.config;
        if (!config || !config.retry || config.retryAttempt >= config.retry) {
            return Promise.reject(error);
        }

        config.retryAttempt = config.retryAttempt + 1;
        const delay = config.retryDelay * config.retryAttempt;

        // Handle specific error cases
        if (error.response?.status === 413) {
            return Promise.reject(new Error('File size too large. Please reduce the file size and try again.'));
        }

        if (error.code === 'ECONNABORTED') {
            console.log(`[LOAMS] Retrying request after timeout. Attempt ${config.retryAttempt} of ${config.retry}`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return pythonApi(config);
        }

        // Handle blob error responses
        if (error.response?.data instanceof Blob) {
            try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                error.details = errorData;
                console.error('[LOAMS] Blob error details:', errorData);
            } catch (e) {
                console.error('[LOAMS] Failed to parse blob error:', e);
                error.details = { error: 'Failed to parse error response' };
            }
        }

        if (error.response?.status === 401) {
            localStorage.clear();
            window.location.href = '/login?session=expired';
        }

        return Promise.reject(error);
    }
);

// Enhanced LOAMS API endpoints
export const loamsApi = {
    previewAndSave: async (formData, saveToDb = false) => {
        console.log('[LOAMS] Starting preview and save request', { saveToDb });
        try {
            // Create a new FormData instance
            const newFormData = new FormData();
            newFormData.append('file', formData.get('file'));
            newFormData.append('save_to_db', saveToDb);

            const response = await pythonApi.post('/preview_and_save', newFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 60000 // Increase timeout for large files
            });

            console.log('[LOAMS] Preview and save successful:', response.data);
            return response;
        } catch (error) {
            console.error('[LOAMS] Preview and save error:', error);
            throw error;
        }
    },

    // In api.js, update the certificates object
    certificates: {


        generatePdf: async (data) => {
            console.log('[LOAMS] Generating completion certificate:', data);
            try {
                const response = await pythonApi.post('/api/certificate/generate-pdf', data, {
                    timeout: 600000, // 10-minute timeout for PDF/ZIP generation
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    responseType: data.multiple ? 'blob' : 'json', // ZIP for multiple, JSON for single
                    retry: 3, // Allow 3 retries for network/server errors
                    retryDelay: 1000 // 1-second delay between retries
                });
    
                if (data.multiple) {
                    // Handle ZIP download
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = response.headers['content-disposition']?.split('filename=')[1] || 'completion_certificates.zip';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } else {
                    return response.data; // Return URL for single certificate
                }
    
                return { success: true };
            } catch (error) {
                console.error('[LOAMS] Certificate generation error:', {
                    message: error.message,
                    status: error.response?.status,
                    data: error.response?.data,
                    config: error.config,
                    request: error.request
                });
    
                // Enhanced error handling for 500
                if (error.response?.status === 500) {
                    throw new Error(`Server error (500): ${error.response.data?.error || 'Internal server error'}`);
                }
    
                throw error;
            }
        },
    
        getMonthAgreements: async (month) => {
            console.log('[LOAMS] Fetching agreements for month:', month);
            try {
              const response = await pythonApi.get('/api/certificate/month-agreements', {
                params: { month }
              });
              return response;
            } catch (error) {
              console.error('[LOAMS] Get month agreements error:', error);
              throw error;
            }
        },
    
        generate: async (data) => {
            console.log('[LOAMS] Generating completion certificate:', data);
            try {
              const response = await pythonApi.post('/api/certificate/generate', data);
              return response;
            } catch (error) {
              console.error('[LOAMS] Certificate generation error:', error);
              throw error;
            }
        },
        
        download: async (certificateData) => {
            try {
              const response = await pythonApi.post('/api/certificate/download', 
                certificateData,
                { responseType: 'blob' }
              );
        
              // Create download
              const blob = new Blob([response.data], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `CC_${certificateData.month}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
        
              return { success: true };
            } catch (error) {
              console.error('[LOAMS] Certificate download error:', error);
              throw error;
            }
        },
    
        // Method to handle bulk certificate generation
        generateBatch: async (monthData) => {
            console.log('[LOAMS] Generating batch certificates for month:', monthData);
            try {
                const response = await pythonApi.post('/api/certificate/batch-generate', 
                    monthData,
                    { 
                        responseType: monthData.download_zip ? 'blob' : 'json',
                        timeout: 600000  // 10-minute timeout for batch processing
                    }
                );
    
                // If requesting ZIP download
                if (monthData.download_zip) {
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `certificates_${monthData.month}.zip`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
    
                    return { success: true };
                }
    
                return response;
            } catch (error) {
                console.error('[LOAMS] Batch certificate generation error:', error);
                throw error;
            }
        },
    
        // Method to fetch service types for filtering
        getServiceTypes: async () => {
            console.log('[LOAMS] Fetching service types');
            try {
                const response = await pythonApi.get('/api/service-types');
                return response;
            } catch (error) {
                console.error('[LOAMS] Service types fetch error:', error);
                throw error;
            }
        },





        list: async (cluster, agreementRef) => {
            console.log('[LOAMS] Fetching certificates for', { cluster, agreementRef });
            try {
                const response = await pythonApi.get('/api/certificate/list', {
                    params: {
                        cluster,
                        agreement_ref: agreementRef
                    }
                });
                console.log('[LOAMS] Found certificates:', response.data?.certificates?.length || 0);
                return response;
            } catch (error) {
                console.error('[LOAMS] Certificate list error:', error);
                throw error;
            }
        },

        lists: async () => {
            console.log('[LOAMS] Fetching all certificates');
            try {
                const response = await pythonApi.get('/api/certificates/all');
                console.log('[LOAMS] Raw response:', response);
                
                if (!response.data) {
                    throw new Error('No data received from server');
                }

                return response.data;
            } catch (error) {
                console.error('[LOAMS] Certificate list error:', {
                    message: error.message,
                    status: error.response?.status,
                    data: error.response?.data
                });
                throw error;
            }
        },
    
        getPreviewUrl: async (certificateKey) => {
            console.log('[LOAMS] Getting preview URL for:', certificateKey);
            try {
                const response = await pythonApi.get(`/api/certificate/preview/${encodeURIComponent(certificateKey)}`);
                return response.data.url;
            } catch (error) {
                console.error('[LOAMS] Preview URL error:', error);
                throw error;
            }
        },
    
        deleteMultiple: async (keys) => {
            console.log('[LOAMS] Deleting certificates:', keys);
            try {
                const response = await pythonApi.post('/api/certificates/delete', { keys });
                return response.data;
            } catch (error) {
                console.error('[LOAMS] Certificate deletion error:', error);
                throw error;
            }
        },

        upload: async (formData) => {
            console.log('[LOAMS] Uploading certificate');
            try {
                const response = await pythonApi.post('/api/certificate/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 60000
                });
                return response;
            } catch (error) {
                console.error('[LOAMS] Certificate upload failed:', error);
                throw error;
            }
        },
    
        downloadMultiple: async (keys) => {
            console.log('[LOAMS] Downloading multiple certificates:', keys);
            try {
                const response = await pythonApi.post('/api/certificates/download-batch', 
                    { keys },
                    { 
                        responseType: 'blob',
                        timeout: 300000, // 5 minutes timeout for large downloads
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/zip'
                        }
                    }
                );
                
                console.log('[LOAMS] Successfully downloaded certificates');
                return response;
            } catch (error) {
                console.error('[LOAMS] Batch download error:', error);
                
                if (error.response?.data) {
                    try {
                        const text = await error.response.data.text();
                        const errorData = JSON.parse(text);
                        throw new Error(errorData.error || 'Download failed');
                    } catch (e) {
                        throw new Error('Failed to download certificates');
                    }
                }
                
                throw error;
            }
        },

        

        



        generate: async (data) => {
            console.log('[LOAMS] Generating completion certificate:', data);
            try {
              const response = await pythonApi.post('/api/certificate/generate', data);
              return response;
            } catch (error) {
              console.error('[LOAMS] Certificate generation error:', error);
              throw error;
            }
          },
        
          download: async (certificateData) => {
            try {
              const response = await pythonApi.post('/api/certificate/download', 
                certificateData,
                { responseType: 'blob' }
              );
        
              // Create download
              const blob = new Blob([response.data], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `CC_${certificateData.month}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
        
              return { success: true };
            } catch (error) {
              console.error('[LOAMS] Certificate download error:', error);
              throw error;
            }
          },



        validateMonth: async (params) => {
            console.log('[LOAMS] Validating certificate month:', params);
            try {
                const response = await pythonApi.post('/api/certificate/validate-month', params);
                return response;
            } catch (error) {
                console.error('[LOAMS] Month validation error:', error);
                throw error;
            }
        },
        checkExists: async (cluster, agreementRef, month) => {
            console.log('[LOAMS] Checking certificate existence:', { cluster, agreementRef, month });
            try {
                const response = await pythonApi.get('/api/certificate/check', {
                    params: {
                        cluster,
                        agreement_ref: agreementRef,
                        month
                    }
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS] Check existence error:', error);
                throw error;
            }
        },
    
        requestApproval: async (data) => {
            console.log('[LOAMS] Requesting certificate approval:', data);
            try {
                const response = await pythonApi.post('/api/certificate/request-approval', data);
                console.log('[LOAMS] Approval request submitted:', response.data);
                return response;
            } catch (error) {
                console.error('[LOAMS] Approval request error:', error);
                throw error;
            }
        },
    
        getApprovalStatus: async (cluster, agreementRef, month) => {
            console.log('[LOAMS] Checking approval status:', { cluster, agreementRef, month });
            try {
                const response = await pythonApi.get('/api/certificate/check', {
                    params: {
                        cluster,
                        agreement_ref: agreementRef,
                        month
                    }
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS] Approval status check error:', error);
                throw error;
            }
        },
    
        // Added the new methods INSIDE the certificates object
        getReport: async (month) => {
            console.log('[LOAMS] Fetching certificate report for month:', month);
            try {
                if (!month || !month.match(/^\d{4}-\d{2}$/)) {
                    throw new Error('Invalid month format. Please use YYYY-MM format.');
                }
                
                const response = await pythonApi.get('/api/certificates/report', {
                    params: { month }
                });
                return response.data;
            } catch (error) {
                console.error('[LOAMS] Certificate report error:', error);
                throw error;
            }
        },

        formatFileSize: (bytes) => {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        
    
        // Method to export report as Excel
        exportReportAsExcel: async (reportData, month) => {
            console.log('[LOAMS] Exporting certificate report as Excel');
            try {
                const monthDate = new Date(`${month}-01`);
                const monthName = monthDate.toLocaleString('default', { month: 'long' });
                const year = monthDate.getFullYear();
                
                // Create workbook and worksheet
                const workbook = XLSX.utils.book_new();
                
                // Format the data for Excel
                const excelData = reportData.agreements.map(agreement => ({
                    'Project': agreement.project,
                    'Agreement Ref': agreement.agreement_ref_no,
                    'Service Type': agreement.service_type,
                    'Contract Start': new Date(agreement.contract_start_date).toLocaleDateString(),
                    'Contract End': new Date(agreement.contract_finish_date).toLocaleDateString(),
                    'Duration (Months)': agreement.duration,
                    'Certificate Status': agreement.certificate_uploaded ? 'Uploaded ✓' : 'Pending ✗'
                }));
                
                // Create worksheet with data
                const worksheet = XLSX.utils.json_to_sheet(excelData);
                
                // Add statistics sheet
                const stats = reportData.statistics;
                const statsData = [
                    ['Report Period', `${monthName} ${year}`],
                    [''],
                    ['Total Service Agreements', stats.total_agreements],
                    ['Certificates Uploaded', stats.total_with_certificates],
                    ['Completion Rate', `${stats.completion_rate}%`],
                    ['Unique Projects', stats.unique_projects],
                    ['Total Certificates', stats.total_certificates]
                ];
                const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
                
                // Set column widths
                const wscols = [
                    {wch: 30}, // Project
                    {wch: 20}, // Agreement
                    {wch: 35}, // Service Type
                    {wch: 15}, // Start Date
                    {wch: 15}, // End Date
                    {wch: 10}, // Duration
                    {wch: 15}  // Status
                ];
                worksheet['!cols'] = wscols;
                
                // Add sheets to workbook
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificates Report');
                XLSX.utils.book_append_sheet(workbook, statsSheet, 'Summary');
                
                // Generate Excel file
                const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                
                // Create download link
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Certificate_Report_${monthName}_${year}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                return true;
            } catch (error) {
                console.error('[LOAMS] Excel export error:', error);
                throw error;
            }
        }
    },


    // Request invoice approval
// Request invoice approval
requestInvoiceApproval: async (requestData) => {
    console.log('[LOAMS] Requesting invoice approval:', requestData);
    try {
      // Validate required fields
      if (!requestData.cluster || !requestData.agreementRef || !requestData.month) {
        throw new Error('Missing required fields for approval request');
      }
  
      // Format the month properly if needed (YYYY-MM)
      let month = requestData.month;
      if (!month.match(/^\d{4}-\d{2}$/)) {
        throw new Error('Invalid month format. Please use YYYY-MM format.');
      }
  
      const response = await pythonApi.post('/api/invoice/request-approval', {
        cluster: requestData.cluster,
        agreement_ref: requestData.agreementRef,
        month: month,
        amount: requestData.amount || 0,
        reason: requestData.reason || 'No Operation'
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });
  
      // Check for errors in response
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
  
      console.log('[LOAMS] Approval request successful:', response.data);
      return response;
    } catch (error) {
      console.error('[LOAMS] Approval request error:', error);
      
      // Handle specific error cases
      if (error.response?.status === 409) {
        throw new Error('An approval request already exists for this project and month');
      }
      if (error.response?.status === 404) {
        throw new Error('Project not found or not available for approval');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.error || 'Invalid approval request data');
      }
      
      throw error;
    }
  },
  
  // Get approvals list for a month
  getApprovalsList: async (month) => {
    console.log('[LOAMS] Getting approvals for month:', month);
    try {
      // Validate month format
      if (!month.match(/^\d{4}-\d{2}$/)) {
        throw new Error('Invalid month format. Please use YYYY-MM format.');
      }
      
      const response = await pythonApi.get('/api/invoice/approvals', {
        params: { month },
        headers: {
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });
  
      // Handle response errors
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
  
      console.log('[LOAMS] Got approvals:', response.data?.approvals?.length || 0);
      return response;
    } catch (error) {
      console.error('[LOAMS] Get approvals error:', error);
      
      // For get operations, return empty array on error to avoid UI failures
      if (error.response?.status === 404) {
        return { data: { approvals: [] } };
      }
      
      throw error;
    }
  },
  
  // Update status of an approval (for admin/super users)
  updateApprovalStatus: async (approvalId, status, comments) => {
    console.log('[LOAMS] Updating approval status:', { approvalId, status });
    try {
      if (!approvalId) {
        throw new Error('Approval ID is required');
      }
      
      if (!['APPROVED', 'REJECTED'].includes(status)) {
        throw new Error('Invalid status. Must be APPROVED or REJECTED');
      }
      
      const response = await pythonApi.put(`/api/invoice/approvals/${approvalId}`, {
        status,
        comments: comments || ''
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });
      
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
      
      console.log(`[LOAMS] Approval ${status.toLowerCase()} successfully`);
      return response;
    } catch (error) {
      console.error('[LOAMS] Update approval status error:', error);
      throw error;
    }
  },
    





// Add this method to your loamsApi object in api.js with a different name

// Invoice Status Excel Upload Method with a different name
uploadStatusExcelFiles: async (formData, isDirectMode = false) => {
  console.log('[LOAMS] Uploading invoice status Excel files', isDirectMode ? '(Direct Bank mode)' : '');
  try {
    // Log what we're uploading for debugging
    for (let pair of formData.entries()) {
      console.log('[LOAMS] FormData contains:', pair[0], 
        pair[1] instanceof File ? `File: ${pair[1].name} (${pair[1].size} bytes)` : pair[1]);
    }
    
    // Create a custom Axios instance just for this request to ensure headers are set correctly
    const uploadInstance = axios.create({
      baseURL: pythonApi.defaults.baseURL,
      timeout: 60000,
      withCredentials: true
    });
    
    // Make the API call WITHOUT setting Content-Type header
    const response = await uploadInstance.post('/api/invoicess-status/upload', formData);
    
    return response;
  } catch (error) {
    console.error('[LOAMS] Status upload error:', error);
    throw error;
  }
},




// Add this to your loamsApi object in api.js

// Get amount mismatch logs
// In your API client (loamsApi)
getAmountMismatchLogs: async (params = {}) => {
  console.log('[API] Fetching amount mismatch logs', params);
  try {
    // Build query parameters for filtering
    const queryParams = new URLSearchParams();
    if (params.invoice_number) {
      queryParams.append('invoice_number', params.invoice_number);
    }
    if (params.start_date) {
      queryParams.append('start_date', params.start_date);
    }
    if (params.end_date) {
      queryParams.append('end_date', params.end_date);
    }
    
    // Use the pythonApi instance instead of direct axios call
    const url = `/api/invoice-amount-logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    console.log('[API] Request URL:', url);
    
    const response = await pythonApi.get(url, {
      headers: {
        'Accept': 'application/json'
      },
      timeout: 30000
    });
    
    return response;
  } catch (error) {
    console.error('[API] Get amount logs error:', error);
    throw error;
  }
},

// Export amount mismatch logs to Excel
exportAmountMismatchLogs: async (params = {}) => {
  console.log('[LOAMS] Exporting amount mismatch logs', params);
  try {
      const queryParams = new URLSearchParams();
      if (params.invoiceNumber) {
          queryParams.append('invoice_number', params.invoiceNumber);
      }
      if (params.startDate) {
          queryParams.append('start_date', params.startDate);
      }
      if (params.endDate) {
          queryParams.append('end_date', params.endDate);
      }
      queryParams.append('export', 'true');
      
      const response = await pythonApi.get(
          `/api/invoice-amount-logs?${queryParams}`,
          {
              responseType: 'blob',
              timeout: 30000
          }
      );
      
      // Create and trigger download
      const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
          ? contentDisposition.split('filename=')[1].replace(/"/g, '')
          : `amount_mismatch_logs_${new Date().toISOString().split('T')[0]}.xlsx`;
          
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
  } catch (error) {
      console.error('[LOAMS] Export amount logs error:', error);
      throw error;
  }
},



    // Add these methods to your loamsApi object invoice no and for that invoice no it will loo

// Invoice Status Update Methods
// In api.js - Replace the existing uploadInvoiceStatusExcel function
uploadInvoiceStatusExcel: async (oracleFile, bankFile, isDirectMode = false) => {
  console.log('[LOAMS] Uploading invoice status Excel files', isDirectMode ? '(Direct Bank mode)' : '');
  try {
      const formData = new FormData();
      
      if (oracleFile) {
          formData.append('oracle_file', oracleFile);
          console.log('[LOAMS] Adding Oracle file:', oracleFile.name, `(${(oracleFile.size / 1024).toFixed(2)} KB)`);
      }
      
      if (bankFile) {
          // In direct mode, use special parameter name to signal backend
          const paramName = isDirectMode ? 'direct_bank_file' : 'bank_file';
          formData.append(paramName, bankFile);
          console.log('[LOAMS] Adding Bank file:', bankFile.name, `(${(bankFile.size / 1024).toFixed(2)} KB)`);
          
          // Add flag for direct bank mode
          if (isDirectMode) {
              formData.append('direct_update', 'true');
          }
      }
      
      // Log FormData contents for debugging
      for (let pair of formData.entries()) {
          console.log('[LOAMS] FormData contains:', pair[0], 
              pair[1] instanceof File ? `File: ${pair[1].name} (${pair[1].size} bytes)` : pair[1]);
      }
      
      // Create a custom Axios instance just for this request to ensure headers are set correctly
      const uploadInstance = axios.create({
          baseURL: pythonApi.defaults.baseURL,
          timeout: 60000,
          withCredentials: true
      });
      
      // Make the API call WITHOUT setting Content-Type header
      const response = await uploadInstance.post('/api/invoice-status/upload', formData);
      
      return response;
  } catch (error) {
      console.error('[LOAMS] Status upload error:', error);
      throw error;
  }
},


// this one is for the downlaod the status template for the uploaded status througgh excel file ... 

downloStatusTemplate: async (templateType = 'oracle') => {
  console.log('[LOAMS] Downloading invoice status template:', templateType);
  try {
      const response = await pythonApi.get(`/api/invoice-status/templates/${templateType}`, {
          responseType: 'blob',
          timeout: 30000 // 30 second timeout
      });
      
      // Create download
      const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set the filename based on template type
      link.download = templateType === 'oracle' 
          ? 'oracle_invoice_status_template.xlsx' 
          : templateType === 'bank'
          ? 'bank_payment_template.xlsx'
          : 'direct_bank_template.xlsx';
          
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
  } catch (error) {
      console.error('[LOAMS] Template download failed:', {
          message: error.message,
          status: error.response?.status,
          code: error.code
      });
      
      throw new Error('Failed to download template: ' + (error.message || 'Unknown error'));
  }
},

// Method to batch update invoice statuses from JSON data
updateInvoiceStatuses: async (statusData) => {
  console.log('[LOAMS] Batch updating invoice statuses:', statusData.length);
  try {
      const response = await pythonApi.post('/api/invoice-status/batch-update', statusData, {
          headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
          },
          timeout: 30000 // 30 second timeout
      });
      
      if (!response.data) {
          throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to update invoice statuses');
      }
      
      console.log('[LOAMS] Batch update successful:', {
          updated: response.data.updated,
          failed: response.data.failed,
          notFound: response.data.notFound,
          amountMismatches: response.data.amountMismatches || 0
      });
      
      return response;
  } catch (error) {
      console.error('[LOAMS] Batch update failed:', {
          message: error.message,
          status: error.response?.status,
          code: error.code
      });
      
      // Handle specific error cases
      if (error.code === 'ECONNABORTED') {
          throw new Error('Update request timed out. Please try again later.');
      }
      
      if (error.response?.data?.message) {
          throw new Error(error.response.data.message);
      }
      
      throw error;
  }
},

// Method to download template files
downloadInvoiceStatusTemplate: async (templateType = 'oracle') => {
    console.log('[LOAMS] Downloading invoice status template:', templateType);
    try {
        const response = await pythonApi.get(`/api/invoice-status/templates/${templateType}`, {
            responseType: 'blob',
            timeout: 30000 // 30 second timeout
        });
        
        // Create download
        const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Set the filename based on template type
        link.download = templateType === 'oracle' 
            ? 'oracle_invoice_status_template.xlsx' 
            : 'bank_payment_template.xlsx';
            
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
    } catch (error) {
        console.error('[LOAMS] Template download failed:', {
            message: error.message,
            status: error.response?.status,
            code: error.code
        });
        
        throw new Error('Failed to download template: ' + (error.message || 'Unknown error'));
    }
},

// Add these OCR-related methods to the loamsApi object in api.js

// OCR API endpoints
// Add these OCR-related methods to the loamsApi object

ocr: {
    // Get all S3 documents with extracted reference numbers
    getAllDocuments: async (searchParams = {}) => {
      console.log('[LOAMS] Fetching all OCR processed documents with params:', searchParams);
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        
        if (searchParams.damacRef) {
          queryParams.append('damac_ref', searchParams.damacRef);
        }
        
        if (searchParams.month) {
          queryParams.append('month', searchParams.month);
        }
        
        if (searchParams.project) {
          queryParams.append('project', searchParams.project);
        }
  
        // Use the correct endpoint
        const response = await pythonApi.get(`/api/ocr/documents${queryParams.toString() ? `?${queryParams}` : ''}`, {
          headers: {
            'Accept': 'application/json'
          },
          timeout: 60000 // 60-second timeout
        });
        
        if (!response.data) {
          throw new Error('Invalid response format from server');
        }
        
        console.log('[LOAMS] Found OCR documents:', response.data.documents?.length || 0);
        
        // Transform document data for UI display
        const transformedData = {
          documents: (response.data.documents || []).map(doc => ({
            id: doc.id || Math.random().toString(36).substring(7),
            key: doc.s3_key,
            name: doc.filename || extractFilenameFromPath(doc.s3_key),
            damacRef: doc.damac_ref || 'Unknown',
            invoiceNo: doc.invoice_no || 'Unknown',
            project: doc.project || 'Unknown',
            cluster: doc.cluster || 'Unknown',
            extractedDate: doc.extracted_date ? new Date(doc.extracted_date).toLocaleDateString() : 'Unknown',
            confidence: doc.confidence || 0,
            url: doc.url,
            preview: doc.preview_url,
            size: doc.size ? formatFileSize(doc.size) : 'Unknown',
            lastModified: doc.last_modified ? new Date(doc.last_modified).toLocaleDateString() : 'Unknown'
          })),
          total: response.data.total || response.data.documents?.length || 0,
          availableProjects: response.data.available_projects || [],
          availableClusters: response.data.available_clusters || [],
          availableMonths: response.data.available_months || []
        };
        
        return { data: transformedData };
      } catch (error) {
        console.error('[LOAMS] Get OCR documents error:', error);
        
        // Handle specific error cases
        if (error.code === 'ECONNABORTED') {
          throw new Error('Request timed out. There may be too many documents to retrieve at once.');
        }
        
        // In case of 404, return empty results instead of throwing error
        if (error.response?.status === 404) {
          return { 
            data: {
              documents: [],
              total: 0,
              availableProjects: [],
              availableClusters: [],
              availableMonths: []
            }
          };
        }
        
        throw error;
      }
    },
    
    // Upload new documents to S3 and process with OCR
    uploadDocuments: async (files, options = {}) => {
      console.log('[LOAMS] Uploading documents for OCR processing:', files.length);
      try {
        if (!files || files.length === 0) {
          throw new Error('No files selected for upload');
        }
        
        const formData = new FormData();
        
        // Add files to form data
        Array.from(files).forEach((file, index) => {
          formData.append('files', file);
        });
        
        // Add options like prefix, project, etc.
        if (options.prefix) {
          formData.append('prefix', options.prefix);
        }
        
        if (options.project) {
          formData.append('project', options.project);
        }
        
        if (options.processImmediately !== undefined) {
          formData.append('process_immediately', options.processImmediately.toString());
        }
        
        const response = await pythonApi.post('/api/ocr/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000, // 5-minute timeout for large uploads
          onUploadProgress: options.onProgress
        });
        
        console.log('[LOAMS] Upload successful:', response.data);
        return response;
      } catch (error) {
        console.error('[LOAMS] Upload error:', error);
        
        // Enhanced error handling
        if (error.code === 'ECONNABORTED') {
          throw new Error('Upload timed out. Try with fewer or smaller files.');
        }
        
        if (error.response?.status === 413) {
          throw new Error('The files are too large. Maximum total size is 50MB.');
        }
        
        throw error;
      }
    },
    
    // Process all unprocessed documents in S3
    processUnprocessedDocuments: async () => {
      console.log('[LOAMS] Processing all unprocessed documents');
      try {
        const response = await pythonApi.post('/api/ocr/process-all', null, {
          timeout: 60000 // 1-minute timeout
        });
        
        console.log('[LOAMS] Processing initiated successfully:', response.data);
        return response;
      } catch (error) {
        console.error('[LOAMS] Processing initiation error:', error);
        throw error;
      }
    },
    
    // Get document preview URL
    getDocumentPreviewUrl: async (s3Key) => {
      console.log('[LOAMS] Getting document preview URL for:', s3Key);
      try {
        if (!s3Key) {
          throw new Error('S3 key is required');
        }
        
        const encodedKey = encodeURIComponent(s3Key);
        const response = await pythonApi.get(`/api/ocr/preview/${encodedKey}`);
        
        if (!response.data || !response.data.url) {
          throw new Error('Invalid preview URL response');
        }
        
        return response.data.url;
      } catch (error) {
        console.error('[LOAMS] Preview URL error:', error);
        throw error;
      }
    },
    
    // Get processing status
    getProcessingStatus: async () => {
      try {
        const response = await pythonApi.get('/api/ocr/status', {
          timeout: 10000 // 10-second timeout
        });
        
        return response.data;
      } catch (error) {
        console.error('[LOAMS] Processing status check failed:', error);
        // Default response if error - assume not processing
        return { is_processing: false, remaining_count: 0 };
      }
    },
    
    // Search for documents by DAMAC reference number
    searchByDamacRef: async (damacRef) => {
      try {
        if (!damacRef) {
          throw new Error('DAMAC reference is required');
        }
        
        const response = await pythonApi.get('/api/ocr/search', {
          params: { damac_ref: damacRef }
        });
        
        return response.data;
      } catch (error) {
        console.error('[LOAMS] Search by DAMAC reference failed:', error);
        throw error;
      }
    },
    
    // Download document
    downloadDocument: async (s3Key) => {
      try {
        if (!s3Key) {
          throw new Error('S3 key is required');
        }
        
        const encodedKey = encodeURIComponent(s3Key);
        const response = await pythonApi.get(`/api/ocr/download/${encodedKey}`, {
          responseType: 'blob'
        });
        
        // Extract filename from S3 key
        const filename = s3Key.split('/').pop() || 'document.pdf';
        
        // Create download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
      } catch (error) {
        console.error('[LOAMS] Download error:', error);
        throw error;
      }
    }
  },




  // Add these methods to your loamsApi object in api.js

// Add to the loamsApi object
invoiceFiles: {
  // List all invoice files with optional search
  listFiles: async (searchTerm = '') => {
      console.log('[LOAMS] Fetching invoice files with search:', searchTerm);
      try {
          const response = await pythonApi.get('/api/invoices/files', {
              params: { search: searchTerm },
              headers: {
                  'Accept': 'application/json'
              },
              timeout: 30000 // 30 second timeout
          });
          
          if (!response.data) {
              throw new Error('Invalid response format from server');
          }
          
          console.log('[LOAMS] Found invoice files:', response.data.files?.length || 0);
          return response.data;
      } catch (error) {
          console.error('[LOAMS] Error fetching invoice files:', error);
          
          // Handle specific error cases
          if (error.code === 'ECONNABORTED') {
              throw new Error('Request timed out. There may be too many files to retrieve at once.');
          }
          
          throw error;
      }
  },
  
  // Get view URL for a specific file
  getViewUrl: async (fileKey) => {
      console.log('[LOAMS] Getting view URL for file:', fileKey);
      try {
          if (!fileKey) {
              throw new Error('File key is required');
          }
          
          const response = await pythonApi.get('/api/invoices/files/view', {
              params: { key: fileKey },
              headers: {
                  'Accept': 'application/json'
              }
          });
          
          if (!response.data || !response.data.url) {
              throw new Error('Failed to get file view URL');
          }
          
          return response.data.url;
      } catch (error) {
          console.error('[LOAMS] Error getting file view URL:', error);
          throw error;
      }
  },
  
  // Open file in new tab
  openFile: async (fileKey) => {
      try {
          const url = await loamsApi.invoiceFiles.getViewUrl(fileKey);
          window.open(url, '_blank');
          return true;
      } catch (error) {
          console.error('[LOAMS] Error opening file:', error);
          throw error;
      }
  },
  
  // Format file size for display
  formatFileSize: (bytes) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
},
  

  





    // Updated certificate methods with a new name: certificateService
certificateService: {
    // Updated certificateService.list implementation
    // In api.js - Updated certificateService.list implementation
list: async () => {
    console.log('[LOAMS] Fetching all certificates');
    try {
        const response = await pythonApi.get('/api/certificates/all');
        console.log('[LOAMS] Raw response:', response);
        
        if (!response.data) {
            throw new Error('No data received from server');
        }
        
        // Transform the response to match expected format
        const formattedData = {
            success: true,
            certificates: (response.data.certificates || []).map(cert => {
                // Extract components from path
                const parts = cert.key.split('/');
                const fileName = parts[parts.length - 1] || '';
                // Get actual cluster name from path (Capital Bay instead of PO_7104_000276)
                const clusterPart = parts[1] || '';
                const poNumber = cert.cluster || parts[2] || '';
                const agreementNo = cert.agreement_no || parts[3] || '';
                
                // Format the date for Dubai time (UTC+4)
                const modifiedDate = new Date(cert.last_modified);
                // Add 4 hours for Dubai time
                modifiedDate.setHours(modifiedDate.getHours() + 4);
                const formattedDate = modifiedDate.toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
                
                // Format size
                const size = typeof cert.size === 'number' 
                    ? loamsApi.certificateService.formatFileSize(cert.size)
                    : cert.size;
                
                return {
                    ...cert,
                    displayName: fileName,
                    clusterName: clusterPart, // This will be "Capital Bay" instead of PO_number
                    poNumber: poNumber,       // Store PO number separately
                    agreementNo,
                    date: modifiedDate,
                    formattedDate,
                    size
                };
            })
        };
        
        return { data: formattedData };
    } catch (error) {
        console.error('[LOAMS] Certificate list error:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data
        });
        throw error;
    }
},


    getPreviewUrl: async (certificateKey) => {
        console.log('[LOAMS] Getting preview URL for:', certificateKey);
        try {
            const response = await pythonApi.get(`/api/certificate/preview/${encodeURIComponent(certificateKey)}`);
            
            if (!response.data || !response.data.url) {
                throw new Error('Invalid preview URL response');
            }
            
            console.log('[LOAMS] Preview URL generated successfully');
            return response.data.url;
        } catch (error) {
            console.error('[LOAMS] Preview URL error:', error);
            throw new Error('Failed to generate preview URL');
        }
    },

    deleteCertificates: async (keys) => {
        console.log('[LOAMS] Deleting certificates:', keys);
        try {
            if (!keys || !Array.isArray(keys) || keys.length === 0) {
                throw new Error('No certificates specified for deletion');
            }
            
            const response = await pythonApi.post('/api/certificates/delete', { keys });
            
            if (!response.data) {
                throw new Error('Invalid response from server');
            }
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to delete certificates');
            }
            
            console.log('[LOAMS] Certificates deleted successfully');
            return response.data;
        } catch (error) {
            console.error('[LOAMS] Certificate deletion error:', error);
            throw new Error(error.message || 'Failed to delete certificates');
        }
    },

    downloadCertificates: async (keys) => {
        console.log('[LOAMS] Downloading multiple certificates:', keys);
        try {
            if (!keys || !Array.isArray(keys) || keys.length === 0) {
                throw new Error('No certificates specified for download');
            }
            
            const response = await pythonApi.post('/api/certificates/download-batch', 
                { keys },
                { 
                    responseType: 'blob',
                    timeout: 300000, // 5 minutes timeout for large downloads
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/zip'
                    }
                }
            );
            
            // Verify proper response format
            if (!response.data) {
                throw new Error('Empty response received');
            }
            
            const contentType = response.headers['content-type'];
            if (!contentType || !contentType.includes('application/zip')) {
                throw new Error('Invalid response type: expected zip file');
            }
            
            console.log('[LOAMS] Successfully downloaded certificates');
            return response;
        } catch (error) {
            console.error('[LOAMS] Batch download error:', error);
            
            // Handle different error types
            if (error.code === 'ECONNABORTED') {
                throw new Error('Download request timed out. Try fewer files or check your connection.');
            }
            
            if (error.response?.data) {
                try {
                    const text = await error.response.data.text();
                    const errorData = JSON.parse(text);
                    throw new Error(errorData.error || 'Download failed');
                } catch (e) {
                    throw new Error('Failed to download certificates');
                }
            }
            
            throw error;
        }
    },

    // Helper function to format file size
    formatFileSize: (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // Add this method to get all certificates
    getAllCertificates: async () => {
        console.log('[LOAMS] Fetching all certificates');
        try {
            const response = await pythonApi.get('/api/certificates/all');
            
            if (!response.data) {
                throw new Error('Invalid response format from server');
            }
            
            // Transform certificates to the expected format
            const formattedCertificates = (response.data.certificates || []).map(cert => {
                const parts = cert.key.split('/');
                const fileName = parts[parts.length - 1];
                const clusterName = parts[2] || '';
                const agreementNo = parts[3] || '';
                
                // Format the date
                const modifiedDate = new Date(cert.last_modified);
                const formattedDate = modifiedDate.toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
                
                return {
                    ...cert,
                    displayName: fileName,
                    clusterName,
                    agreementNo,
                    date: modifiedDate,
                    formattedDate,
                    size: typeof cert.size === 'number' ? formatFileSize(cert.size) : cert.size
                };
            });
            
            return {
                data: {
                    success: true,
                    certificates: formattedCertificates
                }
            };
        } catch (error) {
            console.error('[LOAMS] Get all certificates error:', error);
            throw new Error('Failed to load all certificates');
        }
    },

    // Method to get certificates for a specific cluster
    getClusterCertificates: async (cluster) => {
        console.log('[LOAMS] Fetching certificates for cluster:', cluster);
        try {
            const response = await pythonApi.get(`/api/certificate/list/${cluster}`);
            
            if (!response.data) {
                throw new Error('Invalid response format from server');
            }
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to load certificates');
            }
            
            console.log(`[LOAMS] Found ${response.data.certificates?.length || 0} certificates for cluster`);
            return response;
        } catch (error) {
            console.error('[LOAMS] Get cluster certificates error:', error);
            throw new Error(`Failed to load certificates for cluster: ${cluster}`);
        }
    }
},





    previewExcel: async (formData) => {
        console.log('[LOAMS] Starting preview request');
        try {
            const response = await pythonApi.post('/preview', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            console.log('[LOAMS] Preview successful:', response.data);
            return response;
        } catch (error) {
            console.error('[LOAMS] Preview error:', error);
            throw error;
        }
    },


    downloadStatusTemplate: async () => {
        try {
            const response = await pythonApi.get('/api/status/template', {
                responseType: 'blob'
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Template download error:', error);
            throw error;
        }
    },
    
    // Preview status Excel file
    previewStatusExcel: async (formData) => {
        try {
            const response = await pythonApi.post('/api/status/preview', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 30000 // 30 second timeout
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Status preview error:', error);
            
            // Handle different types of errors
            if (error.response?.status === 500) {
                throw new Error('Server error processing file. Please try again.');
            } else if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            } else if (error.code === 'ECONNABORTED') {
                throw new Error('Request timed out. Please try with a smaller file.');
            } else {
                throw new Error('Failed to preview file');
            }
        }
    },
    
    // Update statuses in batch
    updateStatusBatch: async (formData) => {
        try {
            const response = await pythonApi.post('/api/status/update', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Batch status update error:', error);
            throw error;
        }
    },

    getAllInvoices: async () => {
        console.log('[LOAMS] Fetching all invoices');
        try {
            const response = await pythonApi.get('/invoices');
            console.log('[LOAMS] Fetched invoices:', response.data.length);
            return response;
        } catch (error) {
            console.error('[LOAMS] Get invoices error:', error);
            throw error;
        }
    },

    // Add this method to your loamsApi object in api.js

// Inside loamsApi object, add this method:
getAllInvoiceDetails: async () => {
    console.log('[LOAMS] Fetching all invoice details');
    try {
        // Use pythonApi instead of api to correctly access the Python service
        const response = await pythonApi.get('/api/invoice-details');
        
        if (response.data && Array.isArray(response.data)) {
            console.log('[LOAMS] Found invoice details:', response.data.length);
            
            // Process data to ensure numeric values are properly parsed
            const processedData = response.data.map(item => ({
                ...item,
                contract_value: parseFloat(item.contract_value) || 0,
                basic: parseFloat(item.basic) || 0,
                vat: parseFloat(item.vat) || 0,
                amount: parseFloat(item.amount) || 0,
                Jan_2025: parseFloat(item.Jan_2025) || 0,
                Feb_2025: parseFloat(item.Feb_2025) || 0,
                Mar_2025: parseFloat(item.Mar_2025) || 0,
                Apr_2025: parseFloat(item.Apr_2025) || 0,
                May_2025: parseFloat(item.May_2025) || 0,
                Jun_2025: parseFloat(item.Jun_2025) || 0,
                Jul_2025: parseFloat(item.Jul_2025) || 0,
                Aug_2025: parseFloat(item.Aug_2025) || 0,
                Sep_2025: parseFloat(item.Sep_2025) || 0,
                Oct_2025: parseFloat(item.Oct_2025) || 0,
                Nov_2025: parseFloat(item.Nov_2025) || 0,
                Dec_2025: parseFloat(item.Dec_2025) || 0
            }));
            
            return { data: processedData };
        } else {
            console.warn('[LOAMS] Unexpected response format:', response.data);
            throw new Error('Invalid data format received from server');
        }
    } catch (error) {
        console.error('[LOAMS] Get all invoice details error:', error);
        
        // Fall back to getting data from existing invoices
        try {
            // Get some invoice data to create mock details
            const invoicesResponse = await loamsApi.getAllInvoices();
            if (!invoicesResponse.data || !Array.isArray(invoicesResponse.data)) {
                return { data: [] };
            }
            
            console.log('[LOAMS] Fallback: creating mock data from invoices');
            
            // Create mock details from invoices
            const mockDetails = invoicesResponse.data.slice(0, 20).map(invoice => ({
                id: invoice.id || '',
                invoice_id: invoice.id || '',
                po_no: invoice.agreement_ref_no || '',
                auxillarsss: invoice.auxiliary_no || '',
                accounts_name: invoice.project || '',
                start_date: invoice.contract_start_date || '',
                end_date: invoice.contract_finish_date || '',
                contract_value: parseFloat(invoice.contract_value) || 0,
                basic: parseFloat(invoice.unit_price) || 0,
                vat: parseFloat(invoice.vat_amount) || 0,
                amount: parseFloat(invoice.total_amount) || 0,
                service_type: invoice.service_type || '',
                Jan_2025: 0,
                Feb_2025: 0,
                Mar_2025: 0,
                Apr_2025: 0,
                May_2025: 0,
                Jun_2025: 0,
                Jul_2025: 0,
                Aug_2025: 0,
                Sep_2025: 0,
                Oct_2025: 0,
                Nov_2025: 0,
                Dec_2025: 0
            }));
            
            return { data: mockDetails };
        } catch (fallbackError) {
            console.error('[LOAMS] Fallback for invoice details failed:', fallbackError);
            return { data: [] };
        }
    }
},

getInvoiceDetails: async (poNumber) => {
    console.log('[LOAMS] Fetching invoice details for PO:', poNumber);
    try {
        // Use pythonApi instead of api to correctly access the Python service
        const response = await pythonApi.get(`/api/invoice-details/${poNumber}`);
        
        if (response.data && Array.isArray(response.data)) {
            console.log('[LOAMS] Found invoice details');
            
            // Process data to ensure numeric values are properly parsed
            const processedData = response.data.map(item => ({
                ...item,
                contract_value: parseFloat(item.contract_value) || 0,
                basic: parseFloat(item.basic) || 0,
                vat: parseFloat(item.vat) || 0,
                amount: parseFloat(item.amount) || 0,
                Jan_2025: parseFloat(item.Jan_2025) || 0,
                Feb_2025: parseFloat(item.Feb_2025) || 0,
                Mar_2025: parseFloat(item.Mar_2025) || 0,
                Apr_2025: parseFloat(item.Apr_2025) || 0,
                May_2025: parseFloat(item.May_2025) || 0,
                Jun_2025: parseFloat(item.Jun_2025) || 0,
                Jul_2025: parseFloat(item.Jul_2025) || 0,
                Aug_2025: parseFloat(item.Aug_2025) || 0,
                Sep_2025: parseFloat(item.Sep_2025) || 0,
                Oct_2025: parseFloat(item.Oct_2025) || 0,
                Nov_2025: parseFloat(item.Nov_2025) || 0,
                Dec_2025: parseFloat(item.Dec_2025) || 0
            }));
            
            return { data: processedData };
        } else {
            throw new Error('No invoice details found or invalid format');
        }
    } catch (error) {
        console.error('[LOAMS] Get invoice details error:', error);
        
        // Fallback - get invoice data and transform it
        try {
            const invoiceResponse = await loamsApi.searchInvoicesForEdit(poNumber);
            
            if (!invoiceResponse.data || !Array.isArray(invoiceResponse.data) || invoiceResponse.data.length === 0) {
                throw new Error('No invoices found for this PO number');
            }
            
            const firstInvoice = invoiceResponse.data[0];
            
            // Create a fallback structure matching pts_invoice_details schema
            const fallbackData = [{
                id: null,
                invoice_id: null,
                po_no: poNumber,
                auxillarsss: firstInvoice.auxiliary_no || '',
                accounts_name: firstInvoice.project || '',
                start_date: firstInvoice.contract_start_date || '',
                end_date: firstInvoice.contract_finish_date || '',
                contract_value: parseFloat(firstInvoice.contract_value) || 0,
                basic: parseFloat(firstInvoice.unit_price) || 0,
                vat: parseFloat(firstInvoice.vat_amount) || 0,
                amount: parseFloat(firstInvoice.total_amount) || 0,
                service_type: firstInvoice.service_type || '',
                Jan_2025: 0,
                Feb_2025: 0,
                Mar_2025: 0,
                Apr_2025: 0,
                May_2025: 0,
                Jun_2025: 0,
                Jul_2025: 0,
                Aug_2025: 0,
                Sep_2025: 0,
                Oct_2025: 0,
                Nov_2025: 0,
                Dec_2025: 0
            }];
            
            return { data: fallbackData };
        } catch (fallbackError) {
            console.error('[LOAMS] Fallback error:', fallbackError);
            return { data: [] };
        }
    }
},

exportInvoiceDetailsToExcel: async (filteredData) => {
    console.log('[LOAMS] Starting Excel export');
    try {
      // Verify we have data to export
      if (!filteredData || !Array.isArray(filteredData) || filteredData.length === 0) {
        throw new Error('No data available to export');
      }
      
      console.log(`[LOAMS] Exporting ${filteredData.length} records`);
      
      // Direct approach using fetch API for more control
      const response = await fetch('/api/invoice-details/export-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filteredData),
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log(`[LOAMS] Response blob size: ${blob.size} bytes`);
      
      if (blob.size <= 100) {
        throw new Error(`Received file is too small (${blob.size} bytes)`);
      }
      
      // Get filename from content-disposition header if available
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'invoice_details.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/);
        if (match) filename = match[1];
      }
      
      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      link.remove();
      
      return { success: true, fileName: filename };
    } catch (error) {
      console.error('[LOAMS] Export error:', error);
      throw error;
    }
  },














  



    addInvoiceNote: async (noteData) => {
        console.log('[LOAMS] Adding invoice note');
        try {
            const response = await pythonApi.post('/invoice/notes', noteData);
            console.log('[LOAMS] Note added successfully');
            return response;
        } catch (error) {
            console.error('[LOAMS] Add note error:', error);
            throw error;
        }
    },

    getInvoiceNotes: async (invoiceId) => {
        console.log('[LOAMS] Fetching notes for invoice:', invoiceId);
        try {
            const response = await pythonApi.get(`/invoice/notes/${invoiceId}`);
            console.log('[LOAMS] Notes fetched successfully');
            return response;
        } catch (error) {
            console.error('[LOAMS] Get notes error:', error);
            throw error;
        }
    },

    getMonthlyReports: async () => {
        console.log('[LOAMS] Fetching monthly reports');
        try {
            const response = await pythonApi.get('/reports/monthly');
            console.log('[LOAMS] Reports fetched successfully');
            return response;
        } catch (error) {
            console.error('[LOAMS] Get reports error:', error);
            throw error;
        }
    },



    // Updated exportMonthlyReport function for the loamsApi object
exportMonthlyReport: async (year, month) => {
    console.log('[LOAMS] Exporting monthly report:', { year, month });
    try {
      // Format month with leading zero
      const formattedMonth = String(month).padStart(2, '0');
      
      // First attempt to use the backend export endpoint
      try {
        const response = await pythonApi.get('/api/reports/monthly/export', {
          params: { 
            year,
            month: formattedMonth
          },
          responseType: 'blob',
          timeout: 60000, // 60 second timeout for large report generation
        });
        
        // Get month name for filename
        const date = new Date(year, month-1, 1);
        const monthName = date.toLocaleString('default', { month: 'long' });
        
        // Create download
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Monthly_Report_${year}_${monthName}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('[LOAMS] Monthly report exported successfully via API');
        return { success: true };
      } catch (apiError) {
        console.warn('[LOAMS] API export failed, falling back to client-side export:', apiError);
        // If the API export fails, fall back to client-side export
        return await loamsApi.exportMonthlyReportClientSide(year, month);
      }
    } catch (error) {
      console.error('[LOAMS] Report export error:', error);
      throw new Error('Failed to export monthly report: ' + (error.message || 'Unknown error'));
    }
  },
  
  // New client-side fallback export function
  exportMonthlyReportClientSide: async (year, month) => {
    console.log('[LOAMS] Starting client-side export fallback');
    try {
      // Get the report data first
      const reportData = await loamsApi.getMonthlyReportData(year, month);
      if (!reportData) {
        throw new Error('No report data available');
      }
      
      // Get month name
      const date = new Date(year, month-1, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      
      // Create a new jsPDF instance (client-side PDF generation)
      const { jsPDF } = window.jspdf || await import('jspdf');
      const doc = new jsPDF();
      
      // Add company header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('PHOENICIAN TECHNICAL SERVICES LLC', 105, 20, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Central Plaza, Office 399, Dubai Investment Park 1', 105, 28, { align: 'center' });
      doc.text('+971 4 8882 9464 B4', 105, 34, { align: 'center' });
      
      // Add report title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Report per Project', 105, 45, { align: 'center' });
      doc.setFontSize(12);
      doc.text(`${monthName} ${year}`, 105, 52, { align: 'center' });
      
      // Add summary section
      doc.setFontSize(12);
      doc.text('Summary', 20, 65);
      
      // Create summary table
      const summaryData = [
        ['', 'Count', 'Amount (AED)'],
        ['Active Projects', reportData.summary.activeProjects || 0, formatCurrency(reportData.summary.activeAmount || 0)],
        ['Completion Certificates', reportData.summary.completionCertificates || 0, formatCurrency(reportData.summary.certificateAmount || 0)],
        ['Tax Invoice Issued', reportData.summary.invoicesIssued || 0, formatCurrency(reportData.summary.invoiceAmount || 0)],
        ['Payment Received', reportData.summary.paymentsReceived || 0, formatCurrency(reportData.summary.paymentAmount || 0)]
      ];
      
      doc.autoTable({
        startY: 70,
        head: [summaryData[0]],
        body: summaryData.slice(1),
        styles: { fontSize: 10 },
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0] },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40, halign: 'center' },
          2: { cellWidth: 50, halign: 'right' }
        }
      });
      
      // Add details section
      doc.text('Details', 20, doc.autoTable.previous.finalY + 15);
      
      // Create details table
      const detailsHeaders = [['Cluster', 'PO No.', 'Invoice No', 'CC No', 'Status', 'Amount']];
      const detailsRows = reportData.details.map(item => [
        item.cluster,
        item.poNumber,
        item.invoiceNumber,
        item.ccNumber,
        item.status,
        formatCurrency(item.amount)
      ]);
      
      doc.autoTable({
        startY: doc.autoTable.previous.finalY + 20,
        head: detailsHeaders,
        body: detailsRows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0] },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 20 },
          4: { cellWidth: 30 },
          5: { cellWidth: 30, halign: 'right' }
        },
        didDrawPage: (data) => {
          // Add header to each new page
          if (data.pageNumber > 1) {
            doc.setFontSize(8);
            doc.text(`Monthly Report - ${monthName} ${year}`, 105, 15, { align: 'center' });
          }
        }
      });
      
      // Save the PDF
      doc.save(`Monthly_Report_${year}_${monthName}.pdf`);
      console.log('[LOAMS] Client-side PDF export completed');
      
      return { success: true };
    } catch (error) {
      console.error('[LOAMS] Client-side export error:', error);
      
      // Attempt to generate Excel if PDF generation fails
      try {
        console.log('[LOAMS] Falling back to Excel export');
        
        // Get report data
        const reportData = await loamsApi.getMonthlyReportData(year, month);
        if (!reportData) {
          throw new Error('No report data available');
        }
        
        // Get month name
        const date = new Date(year, month-1, 1);
        const monthName = date.toLocaleString('default', { month: 'long' });
        
        // Import XLSX dynamically if needed
        const XLSX = window.XLSX || await import('xlsx').then(module => module.default);
        if (!XLSX) {
          throw new Error('XLSX library not available');
        }
        
        // Create workbook
        const wb = XLSX.utils.book_new();
        
        // Create summary sheet data
        const summaryData = [
          ['PHOENICIAN TECHNICAL SERVICES LLC'],
          ['Central Plaza, Office 399, Dubai Investment Park 1'],
          ['+971 4 8882 9464 B4'],
          [''],
          ['Monthly Report per Project'],
          [`${monthName} ${year}`],
          [''],
          ['', 'Count', 'Amount (AED)'],
          ['Active Projects', reportData.summary.activeProjects || 0, reportData.summary.activeAmount || 0],
          ['Completion Certificates', reportData.summary.completionCertificates || 0, reportData.summary.certificateAmount || 0],
          ['Tax Invoice Issued', reportData.summary.invoicesIssued || 0, reportData.summary.invoiceAmount || 0],
          ['Payment Received', reportData.summary.paymentsReceived || 0, reportData.summary.paymentAmount || 0]
        ];
        
        // Create details sheet data
        const detailsData = [
          ['Cluster', 'PO No.', 'Invoice No', 'CC No', 'Status', 'Amount', 'Bank']
        ];
        
        reportData.details.forEach(item => {
          detailsData.push([
            item.cluster,
            item.poNumber,
            item.invoiceNumber,
            item.ccNumber,
            item.status,
            item.amount,
            item.bank || ''
          ]);
        });
        
        // Add sheets to workbook
        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
        
        const detailsSheet = XLSX.utils.aoa_to_sheet(detailsData);
        XLSX.utils.book_append_sheet(wb, detailsSheet, 'Details');
        
        // Set column widths
        summarySheet['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 20 }];
        detailsSheet['!cols'] = [
          { wch: 30 }, { wch: 20 }, { wch: 15 }, 
          { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
        ];
        
        // Generate Excel file
        XLSX.writeFile(wb, `Monthly_Report_${year}_${monthName}.xlsx`);
        
        return { success: true };
      } catch (excelError) {
        console.error('[LOAMS] Excel fallback export error:', excelError);
        throw new Error('Failed to export report in any format');
      }
    }
  },
  



    checkExistingInvoices: async (filenames) => {
        try {
            const response = await pythonApi.post('/api/invoices/check-existing', {
                filenames
            });
            return response.data;
        } catch (error) {
            console.error('[LOAMS] Check existing invoices error:', error);
            throw error;
        }
    },

    uploadInvoicesToS3: async (files, onProgress) => {
        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files', file.blob, file.filename);
            });

            const response = await pythonApi.post('/api/invoices/upload-to-s3', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    if (onProgress) {
                        onProgress(percentCompleted);
                    }
                }
            });

            return response.data;
        } catch (error) {
            console.error('[LOAMS] Upload to S3 error:', error);
            throw error;
        }
    },

    generateInvoices: async (formData, onProgress) => {
        console.log('[LOAMS] Starting invoice generation');
        try {
            console.log('[LOAMS] Generate FormData:', {
                hasFile: formData.has('file'),
                fileName: formData.get('file')?.name,
                fileSize: formData.get('file')?.size
            });
    
            const response = await pythonApi.post('/generate', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Accept': '*/*'
                },
                responseType: 'blob',
                timeout: 600000, // 10 minutes timeout for large files
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    console.log(`Upload Progress: ${percentCompleted}%`);
                    if (onProgress) {
                        onProgress(percentCompleted);
                    }
                }
            });
    
            // Get S3 upload results from headers
            const s3Results = JSON.parse(response.headers['x-s3-upload-results']);
            console.log('[LOAMS] S3 Upload Results:', s3Results);
    
            return {
                data: response.data,
                headers: response.headers,
                s3Results
            };
    
        } catch (error) {
            if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
                console.error('[LOAMS] Network error:', error);
                throw new Error('Network error occurred. Please try again or check your connection.');
            }
            console.error('[LOAMS] Generation error:', error);
            throw error;
        }
    },

    searchInvoices: async (searchTerm) => {
        console.log('[LOAMS] Searching invoices:', searchTerm);
        try {
            // If number only is entered, add LOAMS- prefix
            const formattedSearch = searchTerm.includes('LOAMS-') ? 
                searchTerm : `LOAMS-${searchTerm}`;
                
            // Add proper error handling
            const response = await pythonApi.get('/api/invoices/search', {
                params: { search: formattedSearch },
                validateStatus: function (status) {
                    return status < 500; // Accept any status less than 500
                }
            });
    
            if (response.data.error) {
                throw new Error(response.data.error);
            }
    
            console.log('[LOAMS] Search results:', response.data);
            return response;
        } catch (error) {
            console.error('[LOAMS] Search error:', error);
            throw error;
        }
    },










    






    // Add these functions to your loamsApi object in api.js

    searchInvoicesForEdit: async (poNumber) => {
        console.log('[LOAMS] Starting edit search with PO:', poNumber);
        try {
            const response = await pythonApi.get('/api/invoices/edit/search', {
                params: { po_number: poNumber },
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
    
            if (response.data && Array.isArray(response.data)) {
                console.log('[LOAMS] Found', response.data.length, 'invoices');
            } else {
                console.warn('[LOAMS] Unexpected response format:', response.data);
            }
    
            return response;
        } catch (error) {
            console.error('[LOAMS] Edit search error:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            throw error;
        }
    },
    
    // Update invoices for a PO number
    updateInvoicesForEdit: async (poNumber, updateData) => {
        console.log('[LOAMS] 🔍 Starting update for PO:', poNumber);
        console.log('[LOAMS] 🔍 Update data:', JSON.stringify(updateData, null, 2));
        
        try {
            // Validate required data
            if (!poNumber || !updateData) {
                throw new Error('Missing required data for update');
            }
    
            // Log the payload being sent to the backend
            const payload = {
                po_number: poNumber,
                update_data: updateData
            };
            console.log('[LOAMS] 🔍 Request payload:', JSON.stringify(payload, null, 2));
    
            // Make the API call
            const startTime = new Date();
            console.log(`[LOAMS] 🔍 Making request to /api/invoices/edit/update at ${startTime.toISOString()}`);
            
            const response = await pythonApi.put('/api/invoices/edit/update', {
                po_number: poNumber,
                update_data: updateData
            }, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
    
            const endTime = new Date();
            const duration = endTime - startTime;
            console.log(`[LOAMS] 🔍 Response received after ${duration}ms:`, response.data);
    
            if (response.data.error) {
                console.error('[LOAMS] ❌ Error in response:', response.data.error);
                throw new Error(response.data.error);
            }
    
            return response;
        } catch (error) {
            console.error('[LOAMS] ❌ Update error:', error.message);
            console.error('[LOAMS] ❌ Error details:', error.response?.data || 'No response data');
            throw error;
        }
    },

    // Keep only this version and remove the other one
updateInvoiceStatus: async (invoiceId, statusData) => {
    console.log('[LOAMS] Updating invoice status:', { invoiceId, statusData });
    try {
        // Add validation
        if (!invoiceId) {
            throw new Error('Invoice ID is required');
        }

        if (!statusData) {
            throw new Error('Status data is required');
        }

        // Add data logging
        console.log('[LOAMS] Request payload:', {
            url: `/api/invoices/${invoiceId}/status`,
            method: 'PUT',
            data: statusData
        });

        const response = await pythonApi.put(
            `/api/invoices/${invoiceId}/status`,
            {
                ...statusData,
                bank: statusData.bank || null,
                // Ensure all required fields are present
                damac_status: statusData.damac_status || null,
                oracle_status: statusData.oracle_status || null,
                payment_status: statusData.payment_status || null,
                // Include all reference numbers
                damac_ref_no: statusData.damac_ref_no || null,
                oracle_ref_no: statusData.oracle_ref_no || null,
                payment_ref_no: statusData.payment_ref_no || null,
                // Include all dates
                damac_date: statusData.damac_date || null,
                oracle_date: statusData.oracle_date || null,
                payment_date: statusData.payment_date || null,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                timeout: 30000 // 30 second timeout
            }
        );

        // Log response
        console.log('[LOAMS] Status update response:', response.data);

        // Validate response
        if (response.data.error) {
            throw new Error(response.data.error);
        }

        return response;
    } catch (error) {
        console.error('[LOAMS] Update status error:', {
            error: error.message,
            status: error.response?.status,
            data: error.response?.data
        });

        // Enhanced error handling
        if (error.response?.status === 404) {
            throw new Error('Invoice not found');
        } else if (error.response?.status === 400) {
            throw new Error(error.response.data.error || 'Invalid status data');
        } else if (error.code === 'ECONNABORTED') {
            throw new Error('Request timed out. Please try again.');
        }

        throw error;
    }
},


downloadInvoiceStatusTemplate: async (templateType = 'oracle') => {
    console.log('[LOAMS] Downloading invoice status template:', templateType);
    try {
        // Use the correct endpoint structure
        const response = await pythonApi.get(`/api/templates/${templateType}`, {
            responseType: 'blob',
            timeout: 30000 // 30 second timeout
        });
        
        // Create download
        const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Set the filename based on template type
        link.download = templateType === 'oracle' 
            ? 'oracle_invoice_status_template.xlsx' 
            : templateType === 'bank'
            ? 'bank_payment_template.xlsx'
            : 'direct_bank_template.xlsx';
            
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
    } catch (error) {
        console.error('[LOAMS] Template download failed:', {
            message: error.message,
            status: error.response?.status,
            code: error.code
        });
        
        throw new Error('Failed to download template: ' + (error.message || 'Unknown error'));
    }
},


    updateAuxiliaryNumber: async (invoiceId, data) => {
        console.log('[LOAMS] Updating auxiliary number:', { invoiceId, data });
        try {
            const response = await pythonApi.put(
                `/api/invoices/${invoiceId}/auxiliary`,
                data
            );
            console.log('[LOAMS] Auxiliary update successful');
            return response;
        } catch (error) {
            console.error('[LOAMS] Update auxiliary error:', error);
            throw error;
        }
    },

    saveAgreementAuxiliary: async (data) => {
        console.log('[LOAMS] Saving agreement auxiliary:', data);
        try {
            const response = await pythonApi.post('/api/agreement/auxiliary', data);
            console.log('[LOAMS] Auxiliary saved successfully');
            return response;
        } catch (error) {
            console.error('[LOAMS] Save auxiliary error:', error);
            throw error;
        }
    },

    addInvoiceNote: async (invoiceId, noteData) => {
        console.log('[LOAMS] Adding invoice note:', { invoiceId, noteData });
        try {
            const response = await pythonApi.post(
                `/api/invoices/${invoiceId}/notes`,
                noteData
            );
            console.log('[LOAMS] Note added successfully');
            return response;
        } catch (error) {
            console.error('[LOAMS] Add note error:', error);
            throw error;
        }
    },

    // In loamsApi.js
getDashboardStats: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await pythonApi.get(`/api/dashboard/stats${queryString ? `?${queryString}` : ''}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        timeout: 30000
      });
  
      if (!response.data) {
        throw new Error('No data received from server');
      }
  
      if (response.data.error) {
        throw new Error(response.data.error);
      }
  
      return response;
  
    } catch (error) {
      console.error('[LOAMS] Dashboard stats error:', error);
      throw error;
    }
  },


  // Monthly Summary API endpoint
getMonthlySummary: async (params = {}) => {
    console.log('[LOAMS] Fetching monthly summary with params:', params);
    try {
        // Build query string with proper parameter handling
        const queryParams = new URLSearchParams();
        
        if (params.year) {
            queryParams.append('year', params.year);
        }
        
        if (params.service_type && params.service_type !== 'all') {
            queryParams.append('service_type', params.service_type);
        }

        // Make the API request with proper configuration
        const response = await pythonApi.get(
            `/api/monthly-summary${queryParams.toString() ? `?${queryParams.toString()}` : ''}`, 
            {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                withCredentials: true,
                timeout: 30000,
                validateStatus: (status) => status < 500 // Accept any status less than 500
            }
        );

        // Validate response
        if (!response.data) {
            throw new Error('No data received from server');
        }

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        // Transform and validate monthly data
        if (response.data.success && response.data.data) {
            const transformedData = {
                monthly: (response.data.data.monthly || []).map(item => ({
                    year: parseInt(item.year) || 0,
                    month: item.month?.trim() || '',
                    invoice_count: parseInt(item.invoice_count) || 0,
                    invoiced_amt: parseFloat(item.invoiced_amt) || 0,
                    oracle_approved_amt: parseFloat(item.oracle_approved_amt) || 0,
                    paid_amt: parseFloat(item.paid_amt) || 0,
                    receivable_amt: parseFloat(item.receivable_amt) || 0,
                    landscaping_amt: parseFloat(item.landscaping_amt) || 0,
                    swimming_amt: parseFloat(item.swimming_amt) || 0,
                    // Calculate payment percentage for visualization
                    payment_percentage: item.oracle_approved_amt > 0 
                        ? (item.paid_amt / item.oracle_approved_amt * 100).toFixed(2) 
                        : 0
                })),
                totals: {
                    invoice_count: parseInt(response.data.data.totals?.invoice_count) || 0,
                    invoiced_amt: parseFloat(response.data.data.totals?.invoiced_amt) || 0,
                    oracle_approved_amt: parseFloat(response.data.data.totals?.oracle_approved_amt) || 0,
                    paid_amt: parseFloat(response.data.data.totals?.paid_amt) || 0,
                    receivable_amt: parseFloat(response.data.data.totals?.receivable_amt) || 0,
                    landscaping_amt: parseFloat(response.data.data.totals?.landscaping_amt) || 0,
                    swimming_amt: parseFloat(response.data.data.totals?.swimming_amt) || 0
                }
            };

            // Sort monthly data by date
            transformedData.monthly.sort((a, b) => {
                const dateA = new Date(`${a.year}-${a.month}-01`);
                const dateB = new Date(`${b.year}-${b.month}-01`);
                return dateA - dateB;
            });

            // Return transformed data with success status
            return {
                ...response,
                data: {
                    success: true,
                    data: transformedData
                }
            };
        }

        return response;

    } catch (error) {
        console.error('[LOAMS] Monthly summary error:', error);
        
        // Handle specific error cases
        if (error.response?.status === 404) {
            throw new Error('Monthly summary service not available');
        }
        
        if (error.code === 'ECONNABORTED') {
            throw new Error('Request timed out. Please try again.');
        }
        
        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }

        if (error.response?.status === 400) {
            throw new Error('Invalid parameters provided');
        }
        
        // Generic error handling
        throw new Error(error.message || 'Failed to fetch monthly summary data');
    }
},

    getContractDateRange: async () => {
        try {
            console.log('[LOAMS] Fetching contract date range');
            
            const response = await pythonApi.get('/api/contract-date-range', {
                headers: {
                    'Accept': 'application/json'
                }
            });

            console.log('[LOAMS] Contract date range response:', response.data);

            if (!response.data) {
                throw new Error('No date range received');
            }

            return response.data;

        } catch (error) {
            console.error('[LOAMS] Contract date range error:', error);
            throw new Error('Failed to fetch contract date range');
        }
    },

    exportDashboardData: async (filters = {}) => {
        try {
            console.log('[LOAMS] Exporting dashboard data with filters:', filters);
            
            const params = new URLSearchParams();
            if (filters.service_type) params.append('service_type', filters.service_type);
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);

            const response = await pythonApi.get(`/api/dashboard/export?${params.toString()}`, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                responseType: 'blob'
            });

            // Create download
            const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `dashboard_report_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true };

        } catch (error) {
            console.error('[LOAMS] Dashboard export error:', error);
            throw new Error('Failed to export dashboard data');
        }
    },

    // Helper function to abort pending requests
    abortController: null,

    cancelPendingRequests: () => {
        if (loamsApi.abortController) {
            loamsApi.abortController.abort();
            loamsApi.abortController = null;
        }
    },

    // Initialize abort controller before making requests
    initAbortController: () => {
        loamsApi.cancelPendingRequests();
        loamsApi.abortController = new AbortController();
        return loamsApi.abortController.signal;
    },



    files: {
        upload: async (file, category = 'documents', subfolder = null, config = {}) => {
          console.log('[LOAMS] Uploading file:', {
            fileName: file.name,
            category,
            subfolder,
            size: file.size
          });
    
          try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', category);
            if (subfolder) {
              formData.append('subfolder', subfolder);
            }
    
            const response = await pythonApi.post('/api/files/upload', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              timeout: 300000, // 5 minutes for large files
              ...config
            });
    
            console.log('[LOAMS] File upload successful:', response.data);
            return response;
          } catch (error) {
            console.error('[LOAMS] File upload failed:', error);
            // Enhance error message based on the error type
            if (error.code === 'ECONNABORTED') {
              throw new Error('Upload timed out. Please try again with a smaller file or check your connection.');
            }
            if (error.response?.status === 413) {
              throw new Error('File is too large. Maximum size allowed is 50MB.');
            }
            throw error;
          }
        },
    
        list: async (params = {}) => {
          console.log('[LOAMS] Listing files with params:', params);
          try {
            const response = await pythonApi.get('/api/files/list', {
              params,
              timeout: 30000 // 30 seconds timeout
            });
    
            // Transform the response to ensure consistent structure
            const files = response.data.files?.map(file => ({
              id: file.id,
              name: file.name,
              size: file.size,
              modified: file.modified,
              category: file.category,
              mime_type: file.mime_type,
              path: file.path
            })) || [];
    
            return {
              data: {
                files,
                total: files.length
              }
            };
          } catch (error) {
            console.error('[LOAMS] List files failed:', error);
            throw error;
          }
        },
    
        download: async (fileId, config = {}) => {
          console.log('[LOAMS] Downloading file:', fileId);
          try {
            const response = await pythonApi.get(`/api/files/download/${fileId}`, {
              responseType: 'blob',
              timeout: 300000, // 5 minutes for large files
              ...config
            });
    
            // If not requesting raw blob, create download
            if (!config.responseType || config.responseType !== 'blob') {
              const blob = new Blob([response.data], {
                type: response.headers['content-type']
              });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = response.headers['content-disposition']?.split('filename=')[1] || 'download';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }
    
            return response;
          } catch (error) {
            console.error('[LOAMS] Download failed:', error);
            if (error.code === 'ECONNABORTED') {
              throw new Error('Download timed out. Please try again later.');
            }
            throw error;
          }
        },
    
        delete: async (fileId) => {
          if (!fileId) {
            throw new Error('File ID is required for deletion');
          }
    
          console.log('[LOAMS] Deleting file:', fileId);
          try {
            const response = await pythonApi.delete(`/api/files/delete/${fileId}`);
            console.log('[LOAMS] File deleted successfully');
            return response;
          } catch (error) {
            console.error('[LOAMS] Delete failed:', error);
            // Enhance error messages based on response
            if (error.response?.status === 404) {
              throw new Error('File not found or already deleted');
            }
            if (error.response?.status === 403) {
              throw new Error('You do not have permission to delete this file');
            }
            throw error;
          }
        },
    
        getStorageInfo: async () => {
          console.log('[LOAMS] Getting storage info');
          try {
            const response = await pythonApi.get('/api/files/storage-info');
            console.log('[LOAMS] Storage info retrieved:', response.data);
            return response;
          } catch (error) {
            console.error('[LOAMS] Get storage info failed:', error);
            throw error;
          }
        },
    
        createBackup: async (options = {}) => {
          console.log('[LOAMS] Creating backup with options:', options);
          try {
            const response = await pythonApi.post('/api/files/backup', options, {
              responseType: 'blob',
              timeout: 600000 // 10 minutes for large backups
            });
    
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `backup_${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
    
            return { success: true };
          } catch (error) {
            console.error('[LOAMS] Backup failed:', error);
            throw error;
          }
        }
      },
    


    





      getMonthlyReportData: async (year, month) => {
        console.log('[LOAMS] Fetching monthly report data:', { year, month });
        try {
            // Format month with leading zero
            const formattedMonth = String(month).padStart(2, '0');
            
            const response = await pythonApi.get('/api/report/monthly', {
                params: { 
                    year,
                    month: formattedMonth
                },
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000  // 30 second timeout
            });
    
            if (!response.data) {
                throw new Error('No data received from server');
            }
    
            console.log('[LOAMS] Received report data:', {
                projects: response.data.summary?.activeProjects || 0,
                invoices: response.data.summary?.invoicesIssued || 0,
                details: response.data.details?.length || 0
            });
    
            return response.data;
    
        } catch (error) {
            console.error('[LOAMS] Monthly report error:', error);
            throw error;
        }
    },
      
      exportMonthlyReport: async (year, month) => {
        try {
          // Use the consistent month name format
          const date = new Date(year, month-1, 1);
          const monthName = date.toLocaleString('default', { month: 'long' });
          const formattedMonth = String(month).padStart(2, '0');
          
          console.log(`[LOAMS] Exporting report for ${monthName} ${year}`);
          
          const response = await pythonApi.get('/api/reports/monthly/export', {
            params: { 
              year,
              month: formattedMonth
            },
            responseType: 'blob'
          });
          
          // Create download
          const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Monthly_Report_${year}_${monthName}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          return { success: true };
        } catch (error) {
          console.error('[LOAMS] Report export error:', error);
          throw new Error('Failed to export monthly report');
        }
      },
















    exportData: async (columns, filters = {}) => {
        try {
            const params = new URLSearchParams();
            
            // Add column selections
            if (columns?.invoice) {
                columns.invoice.forEach(col => params.append('invoice[]', col));
            }
            if (columns?.status) {
                columns.status.forEach(col => params.append('status[]', col));
            }
            
            // Handle serial number range - strip any non-numeric characters
            if (filters.startSerial) {
                const startNum = filters.startSerial.replace(/[^0-9]/g, '');
                if (startNum) {
                    params.append('start_serial', startNum);
                }
            }
            if (filters.endSerial) {
                const endNum = filters.endSerial.replace(/[^0-9]/g, '');
                if (endNum) {
                    params.append('end_serial', endNum);
                }
            }
            
            // Handle service type
            if (filters.serviceType && filters.serviceType !== 'all') {
                params.append('service_type', filters.serviceType);
            }

            // Handle months with validation
            if (filters.months?.length > 0) {
                filters.months.forEach(month => {
                    // Validate month format (YYYY-MM)
                    if (/^\d{4}-\d{2}$/.test(month)) {
                        params.append('months[]', month);
                    }
                });
            }

            console.log('[LOAMS] Export request params:', params.toString());

            const response = await pythonApi.get('/api/export', {
                params: params,
                paramsSerializer: {
                    indexes: null // Required for array parameters
                },
                timeout: 30000 // 30 seconds timeout for potentially large exports
            });

            // Log success
            console.log('[LOAMS] Export successful:', {
                records: response.data?.length || 0,
                filters: filters
            });

            return response;
        } catch (error) {
            // Enhanced error logging
            console.error('[LOAMS] Export error:', {
                error: error.message,
                status: error.response?.status,
                data: error.response?.data,
                filters: filters
            });

            // Rethrow with more specific message if available
            if (error.response?.data?.details) {
                throw new Error(error.response.data.details);
            }
            throw error;
        }
    },




    // In api.js
generateSingleInvoice: async (formData) => {
    console.log('[LOAMS] Generating single invoice');
    try {
      const response = await pythonApi.post('/generate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': '*/*'
        },
        responseType: 'blob',
        timeout: 60000 // 1 minute timeout
      });
  
      // Verify response type
      const contentType = response.headers['content-type'];
      if (!contentType || !contentType.includes('application/zip')) {
        throw new Error('Invalid response type from server');
      }
  
      console.log('[LOAMS] Single invoice generation successful');
      return response;
  
    } catch (error) {
      console.error('[LOAMS] Single invoice generation error:', error);
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        throw new Error('Network error occurred. Please try again or check your connection.');
      }
      throw error;
    }
  },












    // Add method to get available serial number range
    getSerialRange: async () => {
        try {
            const response = await pythonApi.get('/api/invoice-range');
            return response.data;
        } catch (error) {
            console.error('[LOAMS] Failed to get serial range:', error);
            throw error;
        }
    },


    // Update these methods in the loamsApi object in api.js

// Get all unique clusters
getClusters: async () => {
    console.log('[LOAMS] Fetching clusters');
    try {
        const response = await pythonApi.get('/api/file/clusters');  // Updated endpoint
        console.log('[LOAMS] Found clusters:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get clusters error:', error);
        throw error;
    }
},

// Get agreements for a specific cluster
getClusterAgreements: async (cluster) => {
    console.log('[LOAMS] Fetching agreements for cluster:', cluster);
    try {
        const response = await pythonApi.get(`/api/file/clusters/${cluster}/agreements`);  // Updated endpoint
        console.log('[LOAMS] Found agreements:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get agreements error:', error);
        throw error;
    }
},





getYearlyReportAllData: async () => {
  console.log('[LOAMS] Fetching yearly report data for all years');
  try {
    const response = await pythonApi.get('/api/report/yearly/all', {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      timeout: 60000
    });
    
    // The response is already in the right format, so just return it directly
    return response.data;
  } catch (error) {
    console.error('[LOAMS] All years report error:', error);
    throw error;
  }
},

exportYearlyReportAllYears: async () => {
  console.log('[LOAMS] Exporting yearly report for all years');
  try {
    const response = await pythonApi.get('/api/report/yearly/export/all', {
      responseType: 'blob',
      timeout: 120000 // 2 minute timeout for Excel generation of all years
    });
    
    // Create download
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Yearly_Report_All_Years.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('[LOAMS] All years report export error:', error);
    throw new Error('Failed to export all years report');
  }
},

exportYearlyPdfAllYears: async (yearlyData) => {
  console.log('[LOAMS] Exporting yearly PDF report for all years');
  try {
    // Import necessary libraries
    const jspdfModule = await import('jspdf');
    const jsPDF = jspdfModule.default;
    const autotableModule = await import('jspdf-autotable');
    const autoTable = autotableModule.default;
    
    // Create new PDF document in landscape orientation with A3 format
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a3' // Use A3 for more space to fit all columns properly
    });
    
    // Add company header and title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PHOENICIAN TECHNICAL SERVICES LLC', doc.internal.pageSize.width / 2, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Central Plaza, Office 399, Dubai Investment Park 1', doc.internal.pageSize.width / 2, 28, { align: 'center' });
    doc.text('+971 4 8882 9464 B4', doc.internal.pageSize.width / 2, 34, { align: 'center' });
    
    // Add report title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Yearly Summary Report - All Years', doc.internal.pageSize.width / 2, 45, { align: 'center' });
    
    // Create summary table with data across all years
    if (yearlyData && yearlyData.yearly_data && yearlyData.yearly_data.length > 0) {
      // Add year-by-year summary
      const yearlySummaryHeaders = [
        [
          { content: 'Year', styles: { halign: 'left', fontStyle: 'bold' } },
          { content: 'Active Projects (AED)', styles: { halign: 'right', fontStyle: 'bold' } },
          { content: 'Completion Certificates (AED)', styles: { halign: 'right', fontStyle: 'bold' } },
          { content: 'Tax Invoice Issued (AED)', styles: { halign: 'right', fontStyle: 'bold' } },
          { content: 'Payment Received (AED)', styles: { halign: 'right', fontStyle: 'bold' } },
          { content: 'Balance Receivable (AED)', styles: { halign: 'right', fontStyle: 'bold' } }
        ]
      ];
      
      // Calculate totals across all years
      let totalActiveAmount = 0;
      let totalCcAmount = 0;
      let totalInvoiceAmount = 0;
      let totalPaidAmount = 0;
      let totalReceivableAmount = 0;
      
      // Prepare yearly summary data
      const yearlySummaryData = yearlyData.yearly_data.map(yearData => {
        const year = yearData.year;
        const activeAmount = yearData.summary.activeProjects.amount || 0;
        const ccAmount = yearData.summary.completionCertificates.amount || 0;
        const invoiceAmount = yearData.summary.invoiceProcessed?.amount || 0;
        const paidAmount = yearData.summary.paymentsReceived.amount || 0;
        const receivableAmount = (invoiceAmount - paidAmount) || 0;
        
        // Update totals
        totalActiveAmount += activeAmount;
        totalCcAmount += ccAmount;
        totalInvoiceAmount += invoiceAmount;
        totalPaidAmount += paidAmount;
        totalReceivableAmount += receivableAmount;
        
        return [
          { content: year.toString(), styles: { halign: 'left' } },
          { content: formatCurrency(activeAmount), styles: { halign: 'right' } },
          { content: formatCurrency(ccAmount), styles: { halign: 'right' } },
          { content: formatCurrency(invoiceAmount), styles: { halign: 'right' } },
          { content: formatCurrency(paidAmount), styles: { halign: 'right' } },
          { content: formatCurrency(receivableAmount), styles: { halign: 'right', textColor: receivableAmount > 0 ? [174, 39, 50] : [0, 0, 0] } }
        ];
      });
      
      // Add total row
      yearlySummaryData.push([
        { content: 'Total', styles: { halign: 'left', fontStyle: 'bold' } },
        { content: formatCurrency(totalActiveAmount), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency(totalCcAmount), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency(totalInvoiceAmount), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency(totalPaidAmount), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: formatCurrency(totalReceivableAmount), styles: { halign: 'right', fontStyle: 'bold', textColor: [174, 39, 50] } }
      ]);
      
      // Add yearly summary table
      autoTable(doc, {
        startY: 60,
        head: yearlySummaryHeaders,
        body: yearlySummaryData,
        theme: 'grid',
        styles: { 
          fontSize: 10,
          cellPadding: 3,
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        headStyles: { 
          fillColor: [162, 115, 46], 
          textColor: [255, 255, 255],
          fontSize: 10
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });
      
      // Add detailed year section for each year
      let currentY = doc.autoTable.previous.finalY + 15;
      
      // For each year, create a detailed breakout
      for (const yearData of yearlyData.yearly_data) {
        // Add new page if not enough space
        if (currentY > doc.internal.pageSize.height - 100) {
          doc.addPage();
          currentY = 20;
        }
        
        // Add year header
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Year ${yearData.year} Monthly Breakdown`, 20, currentY);
        currentY += 10;
        
        // Create monthly amount data for this year
        const monthlyAmountHeaders = [
          [
            { content: 'Month', styles: { halign: 'left', fontStyle: 'bold' } },
            { content: 'Active Projects', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: 'Comp. Certificates', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: 'Processed', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: 'Payment Received', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: 'Balance Receivable', styles: { halign: 'right', fontStyle: 'bold' } }
          ]
        ];
        
        // Prepare the monthly data
        const monthlyAmountData = yearData.monthly_details.map(month => {
          // Calculate Balance Receivable as Invoice Processed - Payment Received
          const balanceReceivable = (month.processed_amount || 0) - (month.paid_amount || 0);
          
          return [
            { content: month.month.trim(), styles: { halign: 'left' } },
            { content: formatCurrency(month.active_projects_amount), styles: { halign: 'right' } },
            { content: formatCurrency(month.cc_amount), styles: { halign: 'right' } },
            { content: formatCurrency(month.processed_amount), styles: { halign: 'right' } },
            { content: formatCurrency(month.paid_amount), styles: { halign: 'right' } },
            { content: formatCurrency(balanceReceivable), styles: { halign: 'right', textColor: balanceReceivable > 0 ? [174, 39, 50] : [0, 0, 0] } }
          ];
        });
        
        // Add monthly amount table for this year
        autoTable(doc, {
          startY: currentY,
          head: monthlyAmountHeaders,
          body: monthlyAmountData,
          theme: 'grid',
          styles: { 
            fontSize: 8,
            cellPadding: 2,
            lineColor: [200, 200, 200],
            lineWidth: 0.1
          },
          headStyles: { 
            fillColor: [162, 115, 46], 
            textColor: [255, 255, 255],
            fontSize: 8
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          }
        });
        
        // Update current Y position for next year
        currentY = doc.autoTable.previous.finalY + 20;
      }
    } else {
      // No data available message
      doc.setFontSize(12);
      doc.text('No yearly data available.', 20, 70);
    }
    
    // Add download info at bottom of page
    const now = new Date();
    // Format date according to Dubai time (UTC+4)
    const dubaiTime = new Date(now.getTime() + (4 * 60 * 60 * 1000)); // UTC+4 for Dubai
    const dateStr = dubaiTime.toLocaleString('en-AE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Dubai'
    });
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(`Downloaded by - ${getUserName()} | ${dateStr}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, { align: 'right' });
    
    // Save the PDF
    doc.save(`Yearly_Report_All_Years.pdf`);
    
    return { success: true };
  } catch (error) {
    console.error('[LOAMS] All years PDF export error:', error);
    throw error;
  }
},




// Get invoices for a specific agreement
getAgreementInvoices: async (agreementRef) => {
    console.log('[LOAMS] Fetching invoices for agreement:', agreementRef);
    try {
        const response = await pythonApi.get(`/api/file/agreements/${agreementRef}/invoices`);  // Updated endpoint
        console.log('[LOAMS] Found invoices:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get invoices error:', error);
        throw error;
    }
},

getAllInvoices: async () => {
    console.log('[LOAMS] Fetching all invoices');
    try {
        const response = await pythonApi.get('/api/file/invoices/all');
        console.log('[LOAMS] Found invoices:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get all invoices error:', error);
        throw error;
    }
},




getFileClusters: async () => {
    console.log('[LOAMS] Fetching file clusters');
    try {
        const response = await pythonApi.get('/api/file/clusters');
        console.log('[LOAMS] Found file clusters:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get file clusters error:', error);
        throw error;
    }
  },
  
  getFileClusterAgreements: async (cluster) => {
    console.log('[LOAMS] Fetching file agreements for cluster:', cluster);
    try {
        const response = await pythonApi.get(`/api/file/clusters/${cluster}/agreements`);
        console.log('[LOAMS] Found file agreements:', response.data.length);
        return response;
    } catch (error) {
        console.error('[LOAMS] Get file agreements error:', error);
        throw error;
    }
  },




  

  uploadWithInvoiceCreation: async (formData) => {
    console.log('[LOAMS] Uploading certificate with automatic invoice creation');
    try {
        const response = await pythonApi.post('/api/certificate/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: 60000, // 1 minute timeout for uploads
        });
        
        console.log('[LOAMS] Certificate uploaded successfully:', response.data);
        
        // Check if invoice was created with CC reference
        if (response.data.invoice) {
            console.log('[LOAMS] Invoice automatically created:', {
                serialNumber: response.data.invoice.serial_number,
                invoiceRef: response.data.invoice.invoice_ref,
                ccReference: response.data.invoice.cc
            });
            
            // You could notify the user about the CC reference here
            // Example: toast.success(`Certificate uploaded and invoice created with reference ${response.data.invoice.cc}`);
        }
        
        return response;
    } catch (error) {
        console.error('[LOAMS] Certificate upload failed:', error);
        
        // Enhance error handling for specific cases
        if (error.response?.status === 409) {
            throw new Error('A certificate for this month already exists.');
        }
        
        throw error;
    }
},

checkCertificate: async (cluster, agreementRef, month) => {
    console.log('[LOAMS] Checking certificate:', { cluster, agreementRef, month });
    try {
        const response = await pythonApi.get('/api/certificate/check', {
            params: {
                cluster,
                agreement_ref: agreementRef,
                month
            }
        });
        return response.data;
    } catch (error) {
        console.error('[LOAMS] Certificate check failed:', error);
        throw error;
    }
},






// Monthly Project Report methods
getMonthlyProjectReport: async (year, month) => {
    console.log('[LOAMS] Fetching monthly project report:', { year, month });
    try {
      // Format month with leading zero
      const formattedMonth = String(month).padStart(2, '0');
      
      const response = await pythonApi.get('/api/reports/monthly', {
        params: { 
          year,
          month: formattedMonth
        },
        headers: {
          'Accept': 'application/json'
        },
        timeout: 30000, // 30 second timeout for report generation
        retry: 2,       // Allow 2 retries
        retryDelay: 1000 // Wait 1 second between retries
      });
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      
      console.log('[LOAMS] Successfully fetched monthly report data:', {
        projects: response.data?.summary?.activeProjects || 0,
        invoices: response.data?.summary?.invoicesIssued || 0, 
        details: response.data?.details?.length || 0
      });
      
      return response.data;
    } catch (error) {
      console.error('[LOAMS] Monthly project report error:', error);
      
      // Enhanced error details
      if (error.response?.status === 404) {
        throw new Error('Report service not available');
      }
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Report generation timed out. The selected period may contain too much data.');
      }
      
      throw new Error(error.message || 'Failed to load report data');
    }
  },
  
  exportMonthlyProjectReport: async (year, month) => {
    console.log('[LOAMS] Exporting monthly project report:', { year, month });
    try {
      // Format month with leading zero
      const formattedMonth = String(month).padStart(2, '0');
      const monthName = new Date(year, month-1, 1).toLocaleString('default', { month: 'long' });
      
      // First get the report data
      const reportData = await loamsApi.getMonthlyProjectReport(year, formattedMonth);
      
      // Import XLSX dynamically if needed
      const XLSX = window.XLSX || await import('xlsx').then(module => module.default);
      if (!XLSX) {
        throw new Error('XLSX library not available');
      }
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Create summary sheet
      const summaryData = [
        ['PHOENICIAN TECHNICAL SERVICES LLC'],
        ['Central Plaza, Office 399, Dubai Investment Park 1'],
        ['+971 4 8882 9464 B4'],
        [''],
        ['Monthly Report per Project'],
        [`Year: ${year}`],
        [`Month: ${monthName}`],
        [''],
        ['', 'Count', 'Amount'],
        ['Active Projects', reportData.summary.activeProjects, reportData.summary.activeAmount.toFixed(2)],
        ['Completion Certificates', reportData.summary.completionCertificates, reportData.summary.certificateAmount.toFixed(2)],
        ['Tax Invoice Issued', reportData.summary.invoicesIssued, reportData.summary.invoiceAmount.toFixed(2)],
        ['Payment Received', reportData.summary.paymentsReceived, reportData.summary.paymentAmount.toFixed(2)],
        ['']
      ];
      
      // Create details sheet data
      const detailsData = [
        ['Cluster', 'PO No.', 'Invoice No', 'CC No', 'Status', 'Amount', 'Payment Bank']
      ];
      
      reportData.details.forEach(item => {
        detailsData.push([
          item.cluster,
          item.poNumber,
          item.invoiceNumber,
          item.ccNumber,
          item.status,
          item.amount.toFixed(2),
          item.bank || ''
        ]);
      });
      
      // Add sheets to workbook
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
      
      const detailsSheet = XLSX.utils.aoa_to_sheet(detailsData);
      XLSX.utils.book_append_sheet(wb, detailsSheet, 'Details');
      
      // Style sheets
      summarySheet['!cols'] = [{ wch: 25 }, { wch: 10 }, { wch: 15 }];
      detailsSheet['!cols'] = [
        { wch: 25 }, { wch: 20 }, { wch: 15 }, 
        { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }
      ];
      
      // Generate file and trigger download
      XLSX.writeFile(wb, `Monthly_Report_${year}_${monthName}.xlsx`);
      
      console.log('[LOAMS] Successfully exported monthly report to Excel');
      return { success: true };
    } catch (error) {
      console.error('[LOAMS] Monthly report export error:', error);
      throw new Error(`Failed to export report: ${error.message}`);
    }
  },






  getYearlyReportData: async (year) => {
    console.log('[LOAMS] Fetching yearly report data for', year);
    try {
      const response = await pythonApi.get('/api/report/yearly', {
        params: { year },
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 second timeout for report generation
      });
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      console.log('[LOAMS] Successfully received yearly report data');
      return response;
    } catch (error) {
      console.error('[LOAMS] Yearly report error:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Report service not available');
      }
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Report generation timed out. The selected year may contain too much data.');
      }
      
      throw new Error(error.message || 'Failed to load report data');
    }
  },
  
  exportYearlyReport: async (year) => {
    console.log('[LOAMS] Exporting yearly report for year:', year);
    try {
      const response = await pythonApi.get('/api/report/yearly/export', {
        params: { year },
        responseType: 'blob',
        timeout: 60000 // 1 minute timeout for Excel generation
      });
      
      // Create download
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Yearly_Report_${year}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('[LOAMS] Successfully exported yearly report');
      return { success: true };
    } catch (error) {
      console.error('[LOAMS] Yearly report export error:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Export service not available');
      }
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Export timed out. The report may be too large.');
      }
      
      throw new Error('Failed to export yearly report');
    }
  },


  // Add these functions to your loamsApi object in api.js

// Get list of projects for dropdown
getProjectsList: async (searchTerm = '') => {
    console.log('[LOAMS] Fetching projects list with search:', searchTerm);
    try {
      const response = await pythonApi.get('/api/projects', {
        params: { search: searchTerm },
        headers: {
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 second timeout
      });
  
      if (!response.data) {
        throw new Error('No data received from server');
      }
  
      console.log('[LOAMS] Found projects:', response.data.length);
      return response.data;
    } catch (error) {
      console.error('[LOAMS] Projects list error:', error);
      throw new Error(error.message || 'Failed to load projects list');
    }
  },
  
  // Get project report data
  getProjectReportData: async (poNumber, projectName) => {
    console.log('[LOAMS] Fetching project report data:', { poNumber, projectName });
    try {
      // Build query parameters
      const params = {};
      if (poNumber) params.po_number = poNumber;
      if (projectName) params.project_name = projectName;
      
      const response = await pythonApi.get('/api/report/project', {
        params,
        headers: {
          'Accept': 'application/json'
        },
        timeout: 60000 // 60 second timeout for detailed project data
      });
  
      if (!response.data) {
        throw new Error('No data received from server');
      }
  
      console.log('[LOAMS] Project report data received:', {
        project: response.data.project?.name,
        months: response.data.monthly_details?.length
      });
  
      return response.data;
    } catch (error) {
      console.error('[LOAMS] Project report error:', error);
      
      // Enhanced error handling
      if (error.response?.status === 404) {
        throw new Error('Project not found. Please check the PO number or project name.');
      }
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      throw new Error(error.message || 'Failed to load project data');
    }
  },
  
  // Export project report to Excel
  exportProjectReport: async (poNumber) => {
    console.log('[LOAMS] Exporting project report for PO:', poNumber);
    try {
      if (!poNumber) {
        throw new Error('PO number is required for export');
      }
      
      const response = await pythonApi.get('/api/report/project/export', {
        params: { po_number: poNumber },
        responseType: 'blob',
        timeout: 30000 // 30 second timeout for Excel generation
      });
      
      // Create download
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Project_Report_${poNumber.replace(/[\/\\:*?"<>|]/g, '_')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('[LOAMS] Project report export error:', error);
      throw new Error('Failed to export project report: ' + (error.message || 'Unknown error'));
    }
  },





  














  certificate: {
    upload: async (formData) => {
        console.log('[LOAMS] Uploading certificate');
        try {
            const response = await pythonApi.post('/api/certificate/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 60000, // 1 minute timeout for uploads
            });
            console.log('[LOAMS] Certificate uploaded successfully:', response.data);
            return response;
        } catch (error) {
            console.error('[LOAMS] Certificate upload failed:', error);
            throw error;
        }
    },

    list: async (cluster, agreementRef) => {
        console.log('[LOAMS] Fetching certificates for', { cluster, agreementRef });
        try {
            const response = await pythonApi.get('/api/certificate/list', {
                params: {
                    cluster,
                    agreement_ref: agreementRef
                }
            });
            console.log('[LOAMS] Found certificates:', response.data?.certificates?.length || 0);
            return response;
        } catch (error) {
            console.error('[LOAMS] Certificate list error:', error);
            throw error;
        }
    },

    getPreviewUrl: async (certificateKey) => {
        console.log('[LOAMS] Getting preview URL for:', certificateKey);
        try {
            const response = await pythonApi.get(`/api/certificate/preview/${encodeURIComponent(certificateKey)}`);
            console.log('[LOAMS] Got preview URL');
            return response.data.url;
        } catch (error) {
            console.error('[LOAMS] Preview URL error:', error);
            throw error;
        }
    },

    checkExists: async (cluster, agreementRef, month) => {
        console.log('[LOAMS] Checking certificate existence:', { cluster, agreementRef, month });
        try {
            const response = await pythonApi.get('/api/certificate/list', {
                params: {
                    cluster,
                    agreement_ref: agreementRef
                }
            });
            
            const certificates = response.data?.certificates || [];
            const exists = certificates.some(cert => cert.month === month);
            
            return {
                exists,
                certificate: exists ? 
                    certificates.find(cert => cert.month === month) : 
                    null
            };
        } catch (error) {
            console.error('[LOAMS] Check existence error:', error);
            throw error;
        }
    }
},








// In api.js, update the documents object
documents: {
    getDocument: async (identifier, type) => {
        console.log('[LOAMS] Fetching document:', { identifier, type });
        try {
            // Construct the path based on document type
            let path;
            switch(type) {
                case 'Service Agreement':
                    path = `documents/PO/${identifier}.pdf`;
                    break;
                case 'Completion Certificate':
                    path = `documents/CC-LOAMS/CC-${identifier}.pdf`; // Use CC- prefix
                    break;
                default: // Invoice
                    path = `documents/INVOICES/${identifier}.pdf`;
            }
            
            console.log('[LOAMS] Fetching with path:', path);
            
            const response = await pythonApi.get('/api/documents/view', {
                params: { path }
            });
            return response;
        } catch (error) {
            console.error('[LOAMS] Get document error:', error);
            throw error;
        }
    },




    downloadDocument: async (url) => {
        console.log('[LOAMS] Downloading document');
        try {
            const response = await pythonApi.get(url, {
                responseType: 'blob'
            });

            // Create download
            const blob = new Blob([response.data], { 
                type: response.headers['content-type'] 
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            const fileName = contentDisposition ? 
                contentDisposition.split('filename=')[1].replace(/"/g, '') : 
                'document.pdf';
            
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            
            return { success: true };
        } catch (error) {
            console.error('[LOAMS] Document download failed:', error);
            throw error;
        }
    }
},


getAgreementDetails: async (cluster, agreementRef) => {
  console.log('[LOAMS] Fetching agreement details:', { cluster, agreementRef });
  try {
    const response = await pythonApi.get('/api/agreement-details', {
      params: { cluster, agreementRef },
      headers: {
        'Accept': 'application/json'
      },
      timeout: 30000 // 30 second timeout
    });
    
    console.log('[LOAMS] Agreement details response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[LOAMS] Error fetching agreement details:', error);
    
    // Handle specific error cases
    if (error.response?.status === 404) {
      console.warn(`[LOAMS] No agreement found for ${cluster}/${agreementRef}`);
      return { success: false, error: 'Agreement not found' };
    }
    
    throw error;
  }
},

getServiceType: async (cluster, agreementRef) => {
  console.log('[LOAMS] Fetching service type for:', { cluster, agreementRef });
  try {
    const response = await loamsApi.getAgreementDetails(cluster, agreementRef);
    
    if (response.success && response.data) {
      // Return formatted service type if available, otherwise original service type
      return response.data.formatted_service_type || response.data.service_type || '';
    }
    
    // Client-side fallback formatting if server didn't return a formatted type
    if (response.data?.service_type) {
      return formatServiceType(response.data.service_type);
    }
    
    return '';
  } catch (error) {
    console.error('[LOAMS] Error fetching service type:', error);
    // Return empty string on error (for graceful fallback)
    return '';
  }
},















    // Update this function in the loamsApi object (in api.js)
// Update this function in the loamsApi object (in api.js)
updateInvoice: async (invoiceId, invoiceData) => {
  console.log('[LOAMS] Updating invoice:', { invoiceId, invoiceData });
  try {
      // Add validation for required fields
      if (!invoiceId) {
          throw new Error('Invoice ID is required');
      }

      // Log the update request
      console.log('[LOAMS] Update request payload:', {
          id: invoiceId,
          payeeDetails: {
              payee: invoiceData.payee,
              address: invoiceData.address,
              trn: invoiceData.trn
          },
          otherDetails: {
              agreement_ref_no: invoiceData.agreement_ref_no,
              project: invoiceData.project,
              // ... other fields
          }
      });

      const response = await pythonApi.put(
          `/api/invoices/${invoiceId}`,
          invoiceData,
          {
              headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
              },
              timeout: 30000  // 30 second timeout
          }
      );

      // Handle errors in response
      if (response.data.error) {
          throw new Error(response.data.error);
      }

      // Log success details
      console.log('[LOAMS] Invoice update successful:', {
          status: 'success',
          updatedInvoiceId: invoiceId,
          response: response.data
      });

      return response;
  } catch (error) {
      console.error('[LOAMS] Update invoice error:', {
          error: error.message,
          invoiceId,
          status: error.response?.status
      });

      // Enhanced error handling
      if (error.response?.status === 404) {
          throw new Error('Invoice not found');
      } else if (error.response?.status === 400) {
          throw new Error(error.response.data.error || 'Invalid invoice data');
      } else if (error.code === 'ECONNABORTED') {
          throw new Error('Request timed out. Please try again.');
      }

      throw error;
  }
},



    // In api.js
generateSoa: async (params = {}) => {
    console.log('[LOAMS] Generating SOA with params:', params);
    try {
        const queryParams = new URLSearchParams();
        
        // Handle "All Clusters" case
        if (params.cluster && params.cluster !== 'All Clusters') {
            queryParams.append('cluster', params.cluster);
        }
        
        // Add service type if not 'all'
        if (params.serviceType && params.serviceType !== 'all') {
            queryParams.append('service_type', params.serviceType);
        }

        const response = await pythonApi.get(
            `/api/soa/generate${queryParams.toString() ? `?${queryParams}` : ''}`,
            {
                headers: {
                    'Accept': 'application/json'
                },
                timeout: 30000
            }
        );

        if (!response.data) {
            throw new Error('No data received from server');
        }

        // Transform response if needed
        if (Array.isArray(response.data)) {
            return {
                data: {
                    success: true,
                    data: {
                        rows: response.data,
                        totals: response.data.reduce((acc, row) => {
                            acc.invoiced_amount = (acc.invoiced_amount || 0) + parseFloat(row.invoiced_amount || 0);
                            acc.payment_received = (acc.payment_received || 0) + parseFloat(row.payment_received || 0);
                            acc.balance_forwarded = (acc.balance_forwarded || 0) + parseFloat(row.balance_forwarded || 0);
                            acc.outstanding_balance = (acc.outstanding_balance || 0) + parseFloat(row.outstanding_balance || 0);
                            return acc;
                        }, {}),
                        total_count: response.data.length
                    }
                }
            };
        }

        return response;

    } catch (error) {
        console.error('[LOAMS] SOA generation error:', error);
        throw error;
    }
},

downloadSoa: async (data, fileName = 'statement_of_account.pdf') => {
    try {
        const response = await pythonApi.post('/api/soa/download', data, {
            responseType: 'blob',
            timeout: 60000 // Increased timeout for large PDFs
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true };

    } catch (error) {
        console.error('[LOAMS] SOA download error:', error);
        throw error;
    }
},



login: async (credentials) => {
    try {
        // Regular login
        const response = await api.post('/api/auth/login', credentials);
        
        if (!response.data || !response.data.token) {
            throw new Error('Invalid response format from server');
        }

        const { token, user } = response.data;
        
        // Get avatar URL
        const avatarResponse = null

        // Store authentication data
        const finalToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        localStorage.setItem('token', finalToken);
        localStorage.setItem('user', JSON.stringify({
            ...user,
            displayName: user.username?.split('@')[0] || 'User',
            avatarUrl: avatarResponse.data.avatarUrl
        }));
        localStorage.setItem('isAuthenticated', 'true');
        
        // Set role-based access flags
        const role = user.role.toLowerCase();
        localStorage.setItem('hasAccountsAccess', 
            ['super_admin', 'admin', 'accounts'].includes(role) ? 'true' : 'false'
        );
        localStorage.setItem('hasLoamsAccess', 
            ['super_admin', 'admin', 'accounts', 'engineer', 'loams_viewer'].includes(role) ? 'true' : 'false'
        );
        localStorage.setItem('hasBoqAccess', 
            ['super_admin', 'admin', 'boq_admin'].includes(role) ? 'true' : 'false'
        );
        localStorage.setItem('isEngineer', role === 'engineer' ? 'true' : 'false');
        localStorage.setItem('isSuperAdmin', role === 'super_admin' ? 'true' : 'false');
        localStorage.setItem('isLoamsViewer', role === 'loams_viewer' ? 'true' : 'false');
        
        return response;
    } catch (error) {
        console.error('[AUTH] Login failed:', error);
        localStorage.clear();
        throw error;
    }
},


  


    









// get health of the system 

getHealth: async () => {
    try {
        console.log('[LOAMS] Checking service health');
        const response = await pythonApi.get('/health', {
            timeout: 60000,  // 60 second timeout
            retry: 2,        // Allow 2 retries
            retryDelay: 1000 // Wait 1 second between retries
        });

        if (!response.data) {
            throw new Error('No response data received');
        }

        if (response.data.error) {
            throw new Error(response.data.error);
        }

        console.log('[LOAMS] Health check successful:', response.data);
        return response;
    } catch (error) {
        console.error('[LOAMS] Health check failed:', error);
        
        // Provide more specific error messages
        if (error.code === 'ECONNABORTED') {
            throw new Error('Service is starting up or not responding. Please try again in a moment.');
        } else if (error.response?.status === 500) {
            throw new Error('Service is experiencing internal issues. Please try again later.');
        }
        
        throw error;
    }
}
};

// Auth API endpoints
// Update just the authApi section in your api.js
export const authApi = {
    login: async (credentials) => {
        try {
            const response = await api.post('/api/auth/login', credentials);
            console.log('Login response:', response);
            
            if (!response.data || !response.data.token) {
                throw new Error('Invalid response format from server');
            }

            const { token, user } = response.data;
            
            // Store authentication data
            const finalToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
            localStorage.setItem('token', finalToken);
            localStorage.setItem('user', JSON.stringify({
                ...user,
                displayName: user.username?.split('@')[0] || 'User'
            }));
            localStorage.setItem('isAuthenticated', 'true');
            
            // Set role-based access flags
            const role = user.role.toLowerCase();
            localStorage.setItem('hasAccountsAccess', 
                ['super_admin', 'admin', 'accounts','loams_only'].includes(role) ? 'true' : 'false'
            );
            localStorage.setItem('hasLoamsAccess', 
                ['super_admin', 'admin', 'accounts', 'engineer', 'loams_viewer','loams_only'].includes(role) ? 'true' : 'false'
            );
            localStorage.setItem('hasBoqAccess', 
                ['super_admin', 'admin', 'boq_admin'].includes(role) ? 'true' : 'false'
            );
            localStorage.setItem('isEngineer', role === 'engineer' ? 'true' : 'false');
            localStorage.setItem('isSuperAdmin', role === 'super_admin' ? 'true' : 'false');
            localStorage.setItem('isLoamsViewer', role === 'loams_viewer' ? 'true' : 'false');
            
            return response;
        } catch (error) {
            console.error('[AUTH] Login failed:', error);
            localStorage.clear();
            throw error;
        }
    },
    
    logout: async () => {
        try {
            await api.post('/api/auth/logout');
        } finally {
            localStorage.clear();
            window.location.href = '/login';
        }
    }
};

// Client API endpoints
export const clientApi = {
    getClients: async () => {
        try {
            console.log('[CLIENT] Getting clients');
            const response = await api.get('/api/clients');
            console.log('[CLIENT] Got clients:', response.data.length);
            return response;
        } catch (error) {
            console.error('[CLIENT] Get clients error:', error);
            throw error;
        }
    },
    getClient: async (id) => {
        try {
            const response = await api.get(`/api/clients/${id}`);
            return response;
        } catch (error) {
            console.error('[CLIENT] Get client error:', error);
            throw error;
        }
    },
    createClient: (clientData) => api.post('/api/clients', clientData),
    updateClient: (id, clientData) => api.put(`/api/clients/${id}`, clientData),
    deleteClient: (id) => api.delete(`/api/clients/${id}`)
};

// BOQ API endpoints
export const boqApi = {
    getProjects: () => api.get('/api/boq/projects'),
    getProject: (id) => api.get(`/api/boq/projects/${id}`),
    createProject: (projectData) => api.post('/api/boq/projects', projectData),
    updateProject: (id, projectData) => api.put(`/api/boq/projects/${id}`, projectData),
    deleteProject: (id) => api.delete(`/api/boq/projects/${id}`),
    
    getProjectBills: (projectId) => api.get(`/api/boq/projects/${projectId}/bills`),
    createProjectBill: (projectId, billData) => api.post(`/api/boq/projects/${projectId}/bills`, billData),
    updateProjectBill: (billId, billData) => api.put(`/api/boq/bills/${billId}`, billData),
    deleteProjectBill: (billId) => api.delete(`/api/boq/bills/${billId}`)
};






// OCR API endpoints for DAMAC reference extraction
export const ocrApi = {
    // Get all documents from Portal Extract folder
    listDocuments: async (filters = {}) => {
      console.log('[OCR] Fetching documents with filters:', filters);
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        
        if (filters.damacRef) {
          queryParams.append('damac_ref', filters.damacRef);
        }
        
        if (filters.project) {
          queryParams.append('project', filters.project);
        }
        
        const response = await pythonApi.get(`/api/ocr/documents${queryParams.toString() ? `?${queryParams}` : ''}`, {
          headers: {
            'Accept': 'application/json'
          },
          timeout: 30000 // 30-second timeout
        });
  
        if (!response.data) {
          throw new Error('Invalid response format from server');
        }
  
        console.log('[OCR] Found documents:', response.data.documents?.length || 0);
        return response.data;
      } catch (error) {
        console.error('[OCR] Error fetching documents:', error);
        throw error;
      }
    },
  
    // Process a single document to extract DAMAC reference
    processDocument: async (s3Key, options = {}) => {
      console.log('[OCR] Processing document:', s3Key);
      try {
        const response = await pythonApi.post('/api/ocr/process', { 
          s3_key: s3Key,
          project: options.project
        });
  
        return response.data;
      } catch (error) {
        console.error('[OCR] Error processing document:', error);
        throw error;
      }
    },
  
    // Process all unprocessed documents
    processAllDocuments: async () => {
      console.log('[OCR] Processing all documents');
      try {
        const response = await pythonApi.post('/api/ocr/process-all', null, {
          timeout: 60000 // 1-minute timeout for batch processing
        });
        return response.data;
      } catch (error) {
        console.error('[OCR] Error processing all documents:', error);
        throw error;
      }
    },
  
    // Get preview URL for document
    getPreviewUrl: async (s3Key) => {
      console.log('[OCR] Getting preview URL for:', s3Key);
      try {
        const encodedKey = encodeURIComponent(s3Key);
        const response = await pythonApi.get(`/api/ocr/preview/${encodedKey}`);
        
        if (!response.data?.url) {
          throw new Error('No preview URL received');
        }
  
        return response.data.url;
      } catch (error) {
        console.error('[OCR] Error getting preview URL:', error);
        throw error;
      }
    },
  
    // Download document
    downloadDocument: async (s3Key) => {
      console.log('[OCR] Downloading document:', s3Key);
      try {
        const encodedKey = encodeURIComponent(s3Key);
        const response = await pythonApi.get(`/api/ocr/download/${encodedKey}`, {
          responseType: 'blob'
        });
        
        // Extract filename from S3 key
        const filename = s3Key.split('/').pop() || 'document.pdf';
        
        // Create download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
      } catch (error) {
        console.error('[OCR] Error downloading document:', error);
        throw error;
      }
    },
  
    // Upload new documents
    uploadDocuments: async (files, options = {}) => {
      console.log('[OCR] Uploading documents:', files.length);
      try {
        const formData = new FormData();
        
        // Add files to form data
        Array.from(files).forEach(file => {
          formData.append('files', file);
        });
        
        // Add options
        if (options.processImmediately !== undefined) {
          formData.append('process_immediately', options.processImmediately.toString());
        }
        
        if (options.project) {
          formData.append('project', options.project);
        }
        
        if (options.prefix) {
          formData.append('prefix', options.prefix);
        }
  
        const response = await pythonApi.post('/api/ocr/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000, // 5-minute timeout for large uploads
          onUploadProgress: options.onProgress
        });
  
        return response.data;
      } catch (error) {
        console.error('[OCR] Error uploading documents:', error);
        throw error;
      }
    },


    

    

  
    // Get processing status
    getProcessingStatus: async () => {
      try {
        const response = await pythonApi.get('/api/ocr/status', {
          timeout: 10000 // 10-second timeout
        });
        return response.data;
      } catch (error) {
        console.error('[OCR] Error getting processing status:', error);
        return { 
          success: false,
          total_documents: 0,
          processed_documents: 0,
          remaining_documents: 0
        };
      }
    },
  
    // Search for documents by DAMAC reference
    searchByDamacRef: async (damacRef) => {
      try {
        const response = await pythonApi.get('/api/ocr/search', {
          params: { damac_ref: damacRef }
        });
        return response.data;
      } catch (error) {
        console.error('[OCR] Error searching by DAMAC reference:', error);
        throw error;
      }
    },
  
    // Process multiple documents
    processBatch: async (s3Keys) => {
      console.log('[OCR] Processing batch of documents:', s3Keys.length);
      try {
        const results = [];
        
        for (const s3Key of s3Keys) {
          try {
            const response = await pythonApi.post('/api/ocr/process', { s3_key: s3Key });
            results.push({
              s3_key: s3Key,
              success: response.data?.success || false,
              damac_ref: response.data?.damac_ref || null,
              error: response.data?.error || null
            });
          } catch (error) {
            results.push({
              s3_key: s3Key,
              success: false,
              error: error.message || 'Processing failed'
            });
          }
        }
        
        return {
          success: true,
          results,
          successful: results.filter(r => r.success).length,
          failed: results.filter(r => !r.success).length
        };
      } catch (error) {
        console.error('[OCR] Error in batch processing:', error);
        throw error;
      }
    }
  };

  








  


  
// Add this to your api.js file

// PIN API functions
export const pinApi = {
    // Check if user has a PIN set up
    checkPin: async () => {
      try {
        const response = await pythonApi.get('/api/pin/check', {
          headers: {
            'Authorization': localStorage.getItem('token')
          }
        });
        return response;
      } catch (error) {
        console.error('[API] Error checking PIN:', error);
        throw error;
      }
    },
  
    // Set up a new PIN
    setupPin: async (pin) => {
      try {
        const response = await pythonApi.post('/api/pin/setup', 
          { pin },
          {
            headers: {
              'Authorization': localStorage.getItem('token'),
              'Content-Type': 'application/json'
            }
          }
        );
        return response;
      } catch (error) {
        console.error('[API] Error setting up PIN:', error);
        throw error;
      }
    },
  
    // Verify a PIN
    verifyPin: async (pin) => {
      try {
        const response = await pythonApi.post('/api/pin/verify', 
          { pin },
          {
            headers: {
              'Authorization': localStorage.getItem('token'),
              'Content-Type': 'application/json'
            }
          }
        );
        return response;
      } catch (error) {
        console.error('[API] Error verifying PIN:', error);
        throw error;
      }
    },
  
    // Change existing PIN
    changePin: async (currentPin, newPin) => {
      try {
        const response = await pythonApi.post('/api/pin/change', 
          { currentPin, newPin },
          {
            headers: {
              'Authorization': localStorage.getItem('token'),
              'Content-Type': 'application/json'
            }
          }
        );
        return response;
      } catch (error) {
        console.error('[API] Error changing PIN:', error);
        throw error;
      }
    }
  };
export default api;